import * as THREE from 'three';
import type { Track, TrackSample } from '@/world/Track';
import type { TrackTheme } from '@/world/Theme';
import { attachPropModel, NYC_TAXI, vespaStyle } from '@/world/scenery/propModels';
import type { City, RampSpec, TrackFeatureSpec } from '@/config/cities';
import type { BoxInst } from '@/world/scenery/types';
import { placeFeatures, type PlacedFeature } from '@/physics/trackFeatures';
import {
  buildVespaProp,
  doubleDeckerCanvases,
  makeRoadWheel,
  paintedBoxMaterials,
  propHash,
} from '@/world/scenery/propKit';
import type { PlacedShortcut } from '@/physics/shortcuts';
import { buildLandmark } from '@/world/Landmarks';
import { placeScenery } from '@/world/scenery/place';
import { placeLeanTrees } from '@/world/scenery/leanTrees';
import { liftOntoHills } from '@/world/scenery/hills';
import { CAB_YELLOW } from '@/world/scenery/palettes';
import { clearFinishZone } from '@/world/scenery/finishClear';
import { buildFinishPlaza } from '@/world/scenery/finishPlaza';
import { buildNapoliLaundry } from '@/world/scenery/laundryNapoli';
// GS3: the crowd builder moved to scenery/crowd.ts (hats/hair + cheering arms
// + softened shirts) — same name, same call site below.
import { buildCrowd } from '@/world/scenery/crowd';
import {
  buildAdScreens,
  buildBaobabs,
  buildBoats,
  buildBoxes,
  buildBuses,
  buildCabs,
  buildLeanTrees,
  buildFences,
  buildFlat,
  buildHills,
  buildIcebergs,
  buildLanterns,
  buildNeon,
  buildPalms,
  buildPhoneBoxes,
  buildPines,
  buildPlain,
  buildPlainBoxes,
  STREET_GLOW,
  buildPromenade,
  buildRoofs,
  buildSaltMounds,
  buildScooters,
  buildStalls,
  buildStiltHouses,
  buildTrees,
  buildUmbrellas,
  buildVertSigns,
  buildWater,
  courseWaterMaterial,
  groundTexture,
} from '@/world/scenery/builders';
import { BoxKit, buildShortcutMeshes, plankTexture } from '@/world/scenery/shortcutMeshes';
import { clearShortcutCorridors } from '@/world/scenery/shortcutClear';
import { placeShortcuts } from '@/physics/shortcuts';

// Re-export so the render loop can advance the water animation (M37).
export { tickWater } from '@/world/scenery/builders';

/**
 * Scenery.ts — bold low-poly roadside scenery, placed by ZONE along the track so
 * a city's look changes as you drive (NYC: Harlem brownstones → Central Park →
 * Broadway towers → neon Times Square). Each zone's creative logic lives in
 * scenery/zones.ts; the instanced mesh assembly in scenery/builders.ts.
 * Everything is INSTANCED (a handful of draw calls total) to stay smooth on a
 * mid-range phone, and it's all swappable for the Design pass.
 *
 * Deterministic: a city's fixed seed means its scenery is always laid out the same.
 */

export function buildScenery(track: Track, city: City, theme: TrackTheme): THREE.Group {
  const { group, steps } = buildScenerySteps(track, city, theme);
  for (const step of steps) step();
  return group;
}

/**
 * Loading round: the SAME build as {@link buildScenery}, exposed as an
 * ordered list of synchronous steps so a caller can run them with yields
 * between (the city build used to be one multi-second main-thread task — a
 * tap during it sat in the event queue until the whole city existed). The
 * steps run in exactly the order the monolithic build ran them, and every
 * builder draws from its own seeded stream (or none), so yielding between
 * them changes NOTHING about the output — byte-identical scenery, just
 * spread across frames. `buildScenery` itself is this list run back-to-back.
 *
 * Step 0 is the pure placement pass (the heaviest single step); each later
 * step assembles one instance family into draw calls and adds it to `group`.
 */
export function buildScenerySteps(
  track: Track,
  city: City,
  theme: TrackTheme,
): { group: THREE.Group; steps: (() => void)[] } {
  const group = new THREE.Group();
  const hw = track.halfWidth;
  const finish = track.finishDistance;
  const steps: (() => void)[] = [];
  // Deferred state shared by the steps (filled by step 0).
  let a!: ReturnType<typeof placeScenery>;
  let plaza!: THREE.Object3D;
  let leanTrees!: ReturnType<typeof placeLeanTrees>;
  let placedShortcuts!: ReturnType<typeof placeShortcuts>;

  steps.push(() => {
  // The pure placement pass (scenery/place.ts, M98) — every zone builder runs
  // in there so the corridor audit + layout tests can drive it headless.
  a = placeScenery(track, city, theme);

  // M97 — the finish plaza: first CLEAR the line's forecourt (zone scenery
  // used to clip into the finish gateways; runs after every builder so the
  // rng sequence — each city's layout — is untouched), then raise the plaza
  // (gantry/grandstands/flags) which also seats its crowd into a.people
  // BEFORE the instanced assembly below.
  clearFinishZone(a, (x, z) => track.project(x, z), finish, hw, city.id);
  // M116b — the secret-shortcut right-of-way: same doctrine as the finish
  // clearing (delete-only, after every builder, rng untouched) — no zone
  // structure may stand in a portal mouth or across the bore/boardwalk.
  placedShortcuts = city.shortcuts?.length ? placeShortcuts(track, city.shortcuts, city.ramp) : [];
  clearShortcutCorridors(a, placedShortcuts);
  plaza = buildFinishPlaza(track, city, theme, a);

  // M98 — leaning overhang trees: the environment bleeding over the track as
  // high-hanging canopies (own rng stream + own placement rules; the hills
  // that used to bulge over the road are clamped clear in the zones now).
  leanTrees = placeLeanTrees(track, city);

  // TREE ROUND — THE UN-BURIAL PASS. Everything is planted at ROAD grade while
  // the hill domes rise over it from their own centres, so trees inside a
  // dome's footprint were swallowed (measured in M118: Rio 251, NYC 258,
  // Patagonia 204, Dakar 41). scenery/hills.liftOntoHills stands them on the
  // slope instead — Y-ONLY and rng-free, so no count and no x/z moves.
  //
  // IT RUNS HERE, LAST, ON PURPOSE: this is the first point where the dome
  // field is FINAL. clampHills and carveLakeBasins move domes, and both
  // clearFinishZone and clearShortcutCorridors DELETE them — lifting earlier
  // would leave a tree standing on a dome that a later pass removed, i.e. it
  // would trade buried trees for floating ones at the most-watched stretch of
  // the course. (finishClear's own buried-tree sweep is unaffected: it tests
  // dome geometry, never the tree's y.)
  for (const list of [a.trees, a.palms, a.pines, a.baobabs]) liftOntoHills(list, a.hills);
  liftOntoHills(leanTrees, a.hills);
  }); // end of step 0 (placement + clearing + plaza layout)

  // ---- Assemble the instance lists into a handful of draw calls — one
  // family per step. ----
  const step = (fn: () => void): void => {
    steps.push(fn);
  };
  step(() => {
    if (a.grass.length) group.add(buildFlat(a.grass, 0.05, 1, 0.04, 'grass'));
  });
  step(() => {
    if (a.sand.length) group.add(buildFlat(a.sand, 0.07, 1, 0.05, 'sand'));
  });
  // GS1 — the water system: every pond slab train welds into ONE merged
  // shoreline mesh (+ organic disc ponds and the city's derived lakes).
  step(() => {
    if (a.ponds.length || city.water?.lakes?.length) {
      const water = buildWater(a, track, city);
      if (water) group.add(water);
    }
  });
  step(() => {
    if (a.promenade.length) group.add(buildPromenade(a.promenade));
  });
  step(() => {
    if (a.hills.length) group.add(buildHills(a.hills));
  });
  step(() => {
    if (a.buildings.length) group.add(buildBoxes(a.buildings, city.id));
  });
  step(() => {
    if (a.adBuildings.length)
      group.add(buildPlainBoxes(a.adBuildings, undefined, STREET_GLOW[city.id]));
  });
  step(() => {
    if (a.plain.length) group.add(buildPlain(a.plain, city.id));
  });
  step(() => {
    if (a.roofs.length) group.add(buildRoofs(a.roofs));
  });
  step(() => {
    if (a.trees.length) for (const m of buildTrees(a.trees)) group.add(m);
  });
  step(() => {
    if (a.palms.length) for (const m of buildPalms(a.palms)) group.add(m);
  });
  step(() => {
    if (a.pines.length)
      for (const m of buildPines(a.pines, city.id === 'patagonia' ? 'conifer' : 'umbrella'))
        group.add(m);
    if (a.baobabs.length) for (const m of buildBaobabs(a.baobabs)) group.add(m);
  });
  step(() => {
    if (a.neon.length) group.add(buildNeon(a.neon));
  });
  step(() => {
    if (a.adScreens.length) for (const m of buildAdScreens(a.adScreens)) group.add(m);
  });
  step(() => {
    if (a.vertSigns.length) for (const m of buildVertSigns(a.vertSigns)) group.add(m);
  });
  step(() => {
    if (a.cabs.length)
      for (const m of buildCabs(a.cabs, city.id === 'mumbai' ? 0xf6c513 : 0x171a22)) group.add(m);
    if (a.buses.length) for (const m of buildBuses(a.buses)) group.add(m);
  });
  step(() => {
    if (a.phoneBoxes.length) for (const m of buildPhoneBoxes(a.phoneBoxes)) group.add(m);
  });
  step(() => {
    if (a.scooters.length) for (const m of buildScooters(a.scooters)) group.add(m);
  });
  step(() => {
    if (a.boats.length) for (const m of buildBoats(a.boats)) group.add(m);
  });
  step(() => {
    if (a.stalls.length) for (const m of buildStalls(a.stalls)) group.add(m);
  });
  step(() => {
    if (a.umbrellas.length) for (const m of buildUmbrellas(a.umbrellas)) group.add(m);
  });
  step(() => {
    if (a.stiltHouses.length) for (const m of buildStiltHouses(a.stiltHouses)) group.add(m);
  });
  step(() => {
    if (a.lanterns.length) for (const m of buildLanterns(a.lanterns)) group.add(m);
  });
  // Napoli-only array, Napoli-owned builder (scenery/laundryNapoli.ts): a real
  // sagging catenary with tilted washing, replacing the straight-rod version.
  step(() => {
    if (a.laundry.length) for (const m of buildNapoliLaundry(a.laundry)) group.add(m);
  });
  step(() => {
    if (a.fences.length) for (const m of buildFences(a.fences)) group.add(m);
  });
  step(() => {
    if (a.icebergs.length) group.add(buildIcebergs(a.icebergs));
  });
  step(() => {
    if (a.saltMounds.length) group.add(buildSaltMounds(a.saltMounds));
  });
  step(() => {
    if (a.people.length) for (const m of buildCrowd(a.people)) group.add(m);
  });
  step(() => {
    group.add(plaza);
  });
  step(() => {
    if (leanTrees.length) for (const m of buildLeanTrees(leanTrees)) group.add(m);
  });

  // Unique hero landmarks (Apollo, Big Ben, Bow Bridge, …). The course-end
  // sample anchors 'finish' placements beyond the end barrier (M97).
  step(() => {
    const endSample = track.sampleAt(track.length);
    for (const lm of city.landmarks ?? []) {
      group.add(buildLandmark(lm.kind, track.sampleAt(lm.at * finish), lm.side, hw, endSample));
    }
  });

  // The launch ramp made visible (NYC's red stairs; other cities restyle it).
  step(() => {
    if (city.ramp) group.add(buildRampSteps(track, city.ramp, finish));
  });

  // Interactive course features (M47): boost pads / slicks / obstacles. Built
  // from the SAME placeFeatures the physics uses, so what you see is exactly
  // what collides. The theme rides along so pads can scale their glow to the
  // city's daylight (GS4b — a night-tuned pad washed out in Napoli's sun).
  step(() => {
    if (city.features?.length) group.add(buildFeatureMeshes(track, city.features, theme, city));
  });

  // GS6 — rooftop kit under the aerial shortcut corridors (NYC highline /
  // Tokyo expressway): AC units, water tanks and the odd vent glow on the
  // towers the deck rides above. Bounded + deterministic (propHash over the
  // already-placed building list — never the layout rng).
  step(() => {
    const roofProps = buildRooftopKit(a.buildings, a.adBuildings, placedShortcuts, city.id);
    if (roofProps) group.add(roofProps);
  });

  // Secret shortcuts (M116): entry portals + transit shells + exit mouths,
  // built from the SAME placeShortcuts the physics uses — the doorway you see
  // IS the gate you must thread.
  step(() => {
    if (placedShortcuts.length) {
      group.add(buildShortcutMeshes(track, placedShortcuts, theme, city.ramp));
    }
  });

  step(() => {
    group.matrixAutoUpdate = false;
  });
  return { group, steps };
}

// ---- Interactive course features (M47) --------------------------------------

/** Glow colour per boost-pad style. */
const PAD_COLORS: Record<string, number> = {
  billboard: 0xffd24a, // Broadway gold
  neon: 0x18d0ff, // Tokyo cyan
  buslane: 0xd23340, // London bus red
  brt: 0xffce3c, // Lagos danfo yellow
  drums: 0x2fd06a, // Rio carnival green
};

// -- Shared canvas helpers (feature visuals only) ------------------------------

/** Tiny deterministic 0..1 hash for canvas art. Feature visuals must NEVER
 *  consume the layout RNG — that would reshuffle every city's scenery. */
function art(i: number): number {
  const s = Math.sin(i * 127.1 + 311.7) * 43758.5453;
  return s - Math.floor(s);
}

function makeCanvas(w: number, h: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return [c, c.getContext('2d') as CanvasRenderingContext2D];
}

function canvasTex(c: HTMLCanvasElement): THREE.CanvasTexture {
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** Smooth CLOSED blob path (midpoint quadratics through jittered radii) —
 *  organic puddle/ice/sand edges instead of a hard "sticker" rectangle. */
function blobPath(
  x: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  seed: number,
): void {
  const LOBES = 10;
  const pts: [number, number][] = [];
  for (let i = 0; i < LOBES; i++) {
    const a = (i / LOBES) * Math.PI * 2;
    const rad = r * (0.7 + 0.3 * art(seed + i));
    pts.push([cx + Math.cos(a) * rad, cy + Math.sin(a) * rad]);
  }
  const mid = (p: [number, number], q: [number, number]): [number, number] => [
    (p[0] + q[0]) / 2,
    (p[1] + q[1]) / 2,
  ];
  x.beginPath();
  const m0 = mid(pts[LOBES - 1], pts[0]);
  x.moveTo(m0[0], m0[1]);
  for (let i = 0; i < LOBES; i++) {
    const nm = mid(pts[i], pts[(i + 1) % LOBES]);
    x.quadraticCurveTo(pts[i][0], pts[i][1], nm[0], nm[1]);
  }
  x.closePath();
}

/** ONE chevron tile for boost pads (M99 rework): the texture repeats along the
 *  pad so arrow size is CONSTANT in world units — a 22 u pad shows ~7 big
 *  arrows instead of 3 stretched ones — and the shared offset scrolls every
 *  frame (tickFeatures) so the arrows FLOW down-course like a real kart-game
 *  booster. Apex points down-canvas: canvas-top maps to up-course (flipY), so
 *  a down-canvas apex reads as pointing down-course — the travel direction.
 *  GS4b: 128px with a HARD white core over a tight halo — the old 64px tile
 *  was all halo, so every arrow read as a blurry smear at racing distance. */
function chevronTexture(): THREE.CanvasTexture {
  const [c, x] = makeCanvas(128, 128);
  x.clearRect(0, 0, 128, 128);
  x.lineCap = 'round';
  x.lineJoin = 'round';
  // Tight halo → crisp bright body → hard white core: resolves from far away
  // AND stays a sharp arrow up close (no bloom needed on Low quality).
  for (const pass of [
    { width: 34, style: 'rgba(255, 255, 255, 0.22)' },
    { width: 22, style: 'rgba(255, 255, 255, 0.75)' },
    { width: 14, style: '#ffffff' },
  ]) {
    x.strokeStyle = pass.style;
    x.lineWidth = pass.width;
    x.beginPath();
    x.moveTo(20, 40);
    x.lineTo(64, 84);
    x.lineTo(108, 40);
    x.stroke();
  }
  const tex = canvasTex(c);
  tex.anisotropy = 4; // stays crisp at the grazing approach angle
  tex.wrapT = THREE.RepeatWrapping; // repeats along the pad; offset animates
  return tex;
}

let chevrons: THREE.CanvasTexture | null = null;

/** World units of pad length per chevron arrow (the repeat density). */
const CHEVRON_SPACING = 3.4;
/** Arrow flow speed — in chevron TILES per second (offset is in tile units). */
const CHEVRON_FLOW = 1.9;
/** Steam-mist rise speed (texture tiles per second, scrolled by tickFeatures). */
const MIST_RISE = 0.16;

/** Advance the boost-pad arrow flow + the steam-mist rise (called once per
 *  rendered frame by Game, exactly like tickWater — shared textures animate
 *  every pad/steam patch at once). Frozen automatically under reduced motion
 *  because Game stops advancing t. */
export function tickFeatures(elapsed: number): void {
  // Increasing offset slides the pattern toward v=0 = local +Z = down-course.
  if (chevrons) chevrons.offset.y = (elapsed * CHEVRON_FLOW) % 1;
  // Mist scrolls DOWN the canvas so the puffs appear to RISE (flipY).
  if (mistScroll) mistScroll.offset.y = -((elapsed * MIST_RISE) % 1);
}

/**
 * GS4b LAYERING LAW — the road-markings overlay ribbon (TrackMesh) sits 0.11
 * above track-node grade (road top +0.04, markings +0.07 over it). Every
 * feature decal/plate must (a) sit ABOVE that ribbon and (b) carry a
 * renderOrder above the ribbon's default 0, or the city's road paint draws
 * OVER the feature. This was the confirmed pad bug (NYC/Rio wore the
 * centreline across their plates) and the reason Patagonia's ice slick was
 * fully INVISIBLE: the 'gravel' roadStyle is an OPAQUE surface restyle with
 * depthWrite on, and the ice decal at +0.05 lost the depth test to it.
 */
const MARKINGS_CLEAR = 0.13;
/** renderOrder for feature decals in the transparent pass (ribbon = 0). */
const DECAL_ORDER = 2;

function buildFeatureMeshes(
  track: Track,
  specs: readonly TrackFeatureSpec[],
  theme: TrackTheme,
  city: City,
): THREE.Group {
  const cityId = city.id;
  const g = new THREE.Group();
  placeFeatures(track, specs).forEach((f, i) => {
    // Local road gradient along the feature (visual only): long pads/decals on
    // a sloping stretch must tilt with the road or one end buries/floats.
    const probe = Math.max(2, f.halfLength * 0.8);
    const yBack = track.heightAt(f.x - f.dirX * probe, f.z - f.dirZ * probe);
    const yFwd = track.heightAt(f.x + f.dirX * probe, f.z + f.dirZ * probe);
    const slopePitch = Math.atan2(yFwd - yBack, probe * 2);
    if (f.kind === 'boost') g.add(boostPadMesh(f, theme, slopePitch));
    else if (f.kind === 'slick') g.add(slickMesh(f, slopePitch, theme));
    else if (f.kind === 'ford') g.add(fordMesh(f, slopePitch, city));
    else g.add(obstacleMesh(f, i, cityId));
  });
  return g;
}

/** Place + orient a feature's root: local +Z faces down-course (§6.1).
 *  `slopePitch` tilts the whole prop to the local road gradient (−pitch about
 *  the yawed X raises the down-course end — the smooth-wedge convention). */
function featureRoot(f: PlacedFeature, lift: number, slopePitch = 0): THREE.Group {
  const root = new THREE.Group();
  root.position.set(f.x, f.y + lift, f.z);
  root.rotation.set(-slopePitch, Math.atan2(f.dirX, f.dirZ), 0, 'YXZ');
  return root;
}

/** Border-frame strip thickness (world units) — CONSTANT so small and large
 *  pads share the same "bolted-on hardware" look. */
const PAD_FRAME = 0.34;
/** Pad plate thickness — a real bevelled hardware plate, proud of the road
 *  paint (its top face clears the markings ribbon by construction). */
const PAD_PLATE_T = 0.14;
const PAD_TOP = MARKINGS_CLEAR + 0.04; // plate top = 0.17 above node grade
/**
 * How far the plate's BASE spreads outboard of its top face (london-ts nit
 * round: "the pad reads as a proud rubber mat lying ON the road").
 *
 * THE HEIGHT IS NOT NEGOTIABLE — the plate top MUST clear the road-markings
 * ribbon (MARKINGS_CLEAR) or the city's paint draws across the pad again (the
 * GS4b layering bug), and its underside must stay below the road surface
 * (+0.04) or the plate visibly floats. That pins the thickness at ~0.14 and
 * leaves 0.13 of side wall standing above the tarmac: measured, a hard
 * vertical black band all the way round, which is exactly the doormat read.
 *
 * So the fix is the PROFILE, not the height: the sides rake out to a feathered
 * lip at grade (0.13 rise over 0.55 = a ~13° chamfer), the way a real steel
 * road plate is bedded so traffic can drive up it. Display only — the collision
 * footprint is placeFeatures' halfWidth/halfLength and is untouched.
 */
const PAD_BEVEL = 0.55;

/**
 * A road plate whose top face is w×l and whose base flares `spread` wider all
 * round: BoxGeometry's bottom ring pushed outboard, then re-normalled so the
 * rake catches light as a chamfer instead of a wall. (RoundedBoxGeometry can't
 * do this — its radius is uniform, so a bevel big enough to read at the base
 * also rounded the top face the arrows sit on.)
 */
function padPlateGeo(w: number, t: number, l: number, spread: number): THREE.BufferGeometry {
  const g = new THREE.BoxGeometry(w, t, l);
  const pos = g.getAttribute('position') as THREE.BufferAttribute;
  const sx = 1 + (2 * spread) / w;
  const sz = 1 + (2 * spread) / l;
  for (let i = 0; i < pos.count; i++) {
    if (pos.getY(i) < 0) {
      pos.setX(i, pos.getX(i) * sx);
      pos.setZ(i, pos.getZ(i) * sz);
    }
  }
  pos.needsUpdate = true;
  g.computeVertexNormals();
  return g;
}

/** Per-style ARROW colour overrides (default = frame colour lerped to white).
 *  Rio's drums pad wears GOLD arrows in a GREEN frame (owner city order). */
const PAD_ARROW_COLORS: Record<string, number> = {
  drums: 0xffd24a,
};

/** Frame-rail height (world units) — the housing the glow lens sits in.
 *  london-ts nit round: 0.075 put the lit border 0.245 above grade, a fat
 *  glossy bar standing proud all the way round — the doormat's rubber trim.
 *  0.03 keeps the housing's own shading (the depth cue a flat MeshBasic band
 *  can never have) while the border stops being a bumper; the glow, which is
 *  what carries the pad's identity up the road, is unchanged. */
const PAD_RAIL_H = 0.03;
/** How far the rails are pulled INBOARD of the plate's top edge, so they sit
 *  on the flat instead of overhanging the new chamfer. */
const PAD_RAIL_INSET = 0.05;

/** Shared pad materials, keyed per (colour, arrow colour, day boost). Lazy Map
 *  so every pad of a style in every city reuses the exact same materials. */
interface PadMats {
  /** LIT rail housing (dark tint of the pad colour) — its own shading is what
   *  gives the border depth instead of a flat sticker band. */
  housing: THREE.MeshStandardMaterial;
  /** The glow lens laid in that housing, soft-edged across its width. */
  lens: THREE.MeshBasicMaterial;
  arrows: THREE.MeshBasicMaterial;
}
let padMats: Map<string, PadMats> | null = null;
let padPlate: THREE.MeshStandardMaterial | null = null;
let padLensGrad: THREE.CanvasTexture | null = null;

/** Cross-section falloff for the frame lens: transparent at the rail edges,
 *  solid down the middle. The M-era frame was ONE flat unlit colour across
 *  its whole width, which at close range read as a saturated sticker band
 *  taped to the road (boost-pad-orbit QA shot); a real light strip has a hot
 *  core that falls off into its housing. Canvas v = across the rail. */
function padLensTexture(): THREE.CanvasTexture {
  const [c, x] = makeCanvas(4, 64);
  const g = x.createLinearGradient(0, 0, 0, 64);
  g.addColorStop(0, 'rgba(255,255,255,0)');
  g.addColorStop(0.22, 'rgba(255,255,255,0.55)');
  g.addColorStop(0.5, 'rgba(255,255,255,1)');
  g.addColorStop(0.78, 'rgba(255,255,255,0.55)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  x.fillStyle = g;
  x.fillRect(0, 0, 4, 64);
  return canvasTex(c);
}

/** Brushed-steel plate skin: directional grind streaks + corner bolt heads +
 *  a darker recessed centre channel where the arrows run. Colour is near-dark
 *  so the hot frame/arrows carry the identity. */
function padPlateTexture(): { map: THREE.CanvasTexture; rough: THREE.CanvasTexture } {
  const [c, x] = makeCanvas(256, 256);
  const [rc, rx] = makeCanvas(256, 256);
  x.fillStyle = '#22252c';
  x.fillRect(0, 0, 256, 256);
  rx.fillStyle = '#b4b4b4'; // roughness ~0.7 base
  rx.fillRect(0, 0, 256, 256);
  // Lengthwise brushed grind streaks (canvas y = along the pad under flipY).
  for (let i = 0; i < 130; i++) {
    const sx = art(i * 3 + 1) * 256;
    const len = 30 + art(i * 5 + 2) * 140;
    const sy = art(i * 7 + 3) * 256;
    const tone = art(i * 11 + 4);
    x.fillStyle = tone > 0.5 ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.10)';
    x.fillRect(sx, sy, 1.5 + art(i * 13) * 2, len);
    rx.fillStyle = tone > 0.5 ? 'rgba(80,80,80,0.25)' : 'rgba(230,230,230,0.2)';
    rx.fillRect(sx, sy, 2, len);
  }
  // Recessed darker centre channel (the arrow runway).
  x.fillStyle = 'rgba(0,0,0,0.3)';
  x.fillRect(52, 10, 152, 236);
  // Bolt heads down both edges — "bolted-on hardware".
  for (let b = 0; b < 7; b++) {
    for (const bx of [24, 232]) {
      const by = 22 + b * 35;
      x.fillStyle = '#0c0e12';
      x.beginPath();
      x.arc(bx, by, 6, 0, Math.PI * 2);
      x.fill();
      x.fillStyle = '#5a616c';
      x.beginPath();
      x.arc(bx - 1.5, by - 1.5, 3.6, 0, Math.PI * 2);
      x.fill();
      rx.fillStyle = '#3c3c3c'; // bolts polished → glossier
      rx.beginPath();
      rx.arc(bx, by, 6, 0, Math.PI * 2);
      rx.fill();
    }
  }
  const rough = new THREE.CanvasTexture(rc);
  rough.colorSpace = THREE.NoColorSpace;
  return { map: canvasTex(c), rough };
}

function padMatsFor(color: number, arrowColor: number | undefined, dayBoost: number): PadMats {
  padMats ??= new Map();
  const key = `${color}:${arrowColor ?? -1}:${dayBoost.toFixed(2)}`;
  let m = padMats.get(key);
  if (!m) {
    chevrons ??= chevronTexture();
    padLensGrad ??= padLensTexture();
    // Arrows lean toward white so they read HOTTER than the frame around them
    // (same hue family, brighter core — like a lit sign vs its neon border).
    const bright =
      arrowColor !== undefined
        ? new THREE.Color(arrowColor).lerp(new THREE.Color(0xffffff), 0.25)
        : new THREE.Color(color).lerp(new THREE.Color(0xffffff), 0.5);
    // GS4b day-city fix: unlit pad colours washed out under a 2.7+ sun once
    // ACES tone mapping compressed them. toneMapped:false + a sun-scaled
    // multiplier keeps the glow language identical in Napoli's noon and
    // Tokyo's night — WITHOUT relying on bloom (must read on Low quality).
    m = {
      // The housing is LIT (MeshStandard): the sun/hemi shade its four faces
      // differently, so the border reads as bolted-on hardware with real
      // thickness — the depth cue a MeshBasic band can never have.
      housing: new THREE.MeshStandardMaterial({
        color: new THREE.Color(color).multiplyScalar(0.2),
        roughness: 0.42,
        metalness: 0.55,
      }),
      lens: new THREE.MeshBasicMaterial({
        color: new THREE.Color(color).multiplyScalar(dayBoost),
        map: padLensGrad,
        transparent: true,
        depthWrite: false,
        toneMapped: false,
      }),
      arrows: new THREE.MeshBasicMaterial({
        color: bright.multiplyScalar(dayBoost),
        map: chevrons,
        transparent: true,
        depthWrite: false,
        toneMapped: false,
      }),
    };
    padMats.set(key, m);
  }
  return m;
}

function boostPadMesh(f: PlacedFeature, theme: TrackTheme, slopePitch: number): THREE.Group {
  const root = featureRoot(f, 0, slopePitch);
  const color = PAD_COLORS[f.style] ?? 0xffd24a;
  // Sun-scaled glow boost: night cities (sun ≤1.8) stay 1.0; bright day
  // cities (Rio 2.9 / Napoli 2.8 / Lagos 2.7 / Mumbai 2.3) climb to ~1.75.
  const dayBoost = Math.min(1.75, Math.max(1, theme.sunIntensity / 1.65));
  const mats = padMatsFor(color, PAD_ARROW_COLORS[f.style], dayBoost);
  if (!padPlate) {
    const skins = padPlateTexture();
    // Site-worn steel, not showroom chrome (the M117 launcher lesson).
    padPlate = new THREE.MeshStandardMaterial({
      map: skins.map,
      roughnessMap: skins.rough,
      roughness: 1,
      metalness: 0.38,
    });
  }
  const fw = f.halfWidth * 2;
  const fl = f.halfLength * 2;
  // The PLATE: a thin box whose top face clears the road-paint ribbon (see
  // MARKINGS_CLEAR) — being opaque and ABOVE it, the paint can never draw
  // across the pad again (depth does the work in the opaque pass) — and whose
  // sides RAKE OUT to a feathered lip at grade (PAD_BEVEL) so it reads bedded
  // into the road rather than laid on top of it.
  const plate = new THREE.Mesh(padPlateGeo(fw, PAD_PLATE_T, fl, PAD_BEVEL), padPlate);
  plate.position.y = PAD_TOP - PAD_PLATE_T / 2;
  root.add(plate);
  const t = PAD_FRAME;
  const iw = Math.max(0.6, fw - 2 * (t + 0.16));
  const il = Math.max(0.6, fl - 2 * (t + 0.16));
  // M99: the arrow strip repeats ONE chevron tile every CHEVRON_SPACING world
  // units (scaled into the plane's V coords), so a long runway pad shows a
  // train of same-sized arrows — and the shared texture offset (tickFeatures)
  // makes the whole train FLOW down-course.
  const arrowLen = Math.max(0.5, il - 0.4);
  const arrowPlane = new THREE.Mesh(
    new THREE.PlaneGeometry(Math.max(0.5, iw - 0.4), arrowLen),
    mats.arrows,
  );
  arrowPlane.rotation.x = -Math.PI / 2;
  arrowPlane.position.y = PAD_TOP + 0.012;
  arrowPlane.renderOrder = DECAL_ORDER + 1; // after the markings ribbon
  const uv = arrowPlane.geometry.getAttribute('uv') as THREE.BufferAttribute;
  const repeats = Math.max(1, Math.round(arrowLen / CHEVRON_SPACING));
  for (let i = 0; i < uv.count; i++) uv.setY(i, uv.getY(i) * repeats);
  uv.needsUpdate = true;
  root.add(arrowPlane);
  // Glowing border frame in the pad colour — raised rails sitting ON the plate
  // (identifiable from far up the road, long before the chevrons resolve).
  // Each rail is a LIT housing carrying a soft-edged glow lens: same colour,
  // same read at distance, but with thickness and a falloff up close.
  // Built running along local X and yawed into place, so the lens gradient
  // always crosses the rail's short axis.
  const bar = (len: number, x: number, z: number, rotY: number): THREE.Group => {
    const grp = new THREE.Group();
    const housing = new THREE.Mesh(new THREE.BoxGeometry(len, PAD_RAIL_H, t), mats.housing);
    housing.position.y = PAD_TOP + PAD_RAIL_H / 2;
    grp.add(housing);
    // The lens is NARROWER than its housing: a dark shoulder either side is
    // what stops the rail reading as one flat painted band.
    const lens = new THREE.Mesh(new THREE.PlaneGeometry(len - 0.03, t * 0.68), mats.lens);
    lens.rotation.x = -Math.PI / 2;
    lens.position.y = PAD_TOP + PAD_RAIL_H + 0.004;
    lens.renderOrder = DECAL_ORDER + 2; // above the plate + the arrow strip
    grp.add(lens);
    grp.position.set(x, 0, z);
    grp.rotation.y = rotY;
    return grp;
  };
  const H = Math.PI / 2;
  const railZ = f.halfLength - t / 2 - PAD_RAIL_INSET;
  const railX = f.halfWidth - t / 2 - PAD_RAIL_INSET;
  root.add(
    bar(fw - 2 * PAD_RAIL_INSET, 0, railZ, 0),
    bar(fw - 2 * PAD_RAIL_INSET, 0, -railZ, 0),
    bar(fl - 2 * t - 2 * PAD_RAIL_INSET, railX, 0, H),
    bar(fl - 2 * t - 2 * PAD_RAIL_INSET, -railX, 0, H),
  );
  return root;
}

// -- Slick patches: each style gets its OWN canvas texture so a driver can name
//    the hazard at speed (dark blobby water / cracked ice / rippled sand /
//    drifting steam) instead of four near-identical tinted rectangles. --------

/** Feather the canvas's outer boundary (destination-in radial falloff): the
 *  blob edge stays irregular, but its alpha now FADES OUT instead of cutting —
 *  the "blurry sticker" read came from a hard bilinear edge on a 128px decal. */
function featherEdge(x: CanvasRenderingContext2D, size: number, from = 0.8): void {
  x.globalCompositeOperation = 'destination-in';
  const g = x.createRadialGradient(
    size / 2,
    size / 2,
    (size / 2) * from,
    size / 2,
    size / 2,
    size / 2,
  );
  g.addColorStop(0, 'rgba(0,0,0,1)');
  g.addColorStop(1, 'rgba(0,0,0,0)');
  x.fillStyle = g;
  x.fillRect(0, 0, size, size);
  x.globalCompositeOperation = 'source-over';
}

/** Irregular wet-asphalt sheet: near-black standing water over the tarmac
 *  (the ENV reflection says "wet"; the canvas keeps it dark + irregular). */
function puddleTexture(): THREE.CanvasTexture {
  const [c, x] = makeCanvas(256, 256);
  x.scale(2, 2); // draw in 128-space at 2× resolution
  // Blobby outline — the transparent canvas edge is what kills the old
  // "glass rectangle lying on the road" read.
  blobPath(x, 64, 64, 58, 3);
  x.fillStyle = 'rgba(9, 13, 19, 0.82)';
  x.fill();
  // Slightly darker DEEP centre: middle reads "standing", edges "damp film".
  blobPath(x, 64, 64, 42, 3);
  x.fillStyle = 'rgba(6, 9, 14, 0.92)';
  x.fill();
  // Two sky-catch sheen streaks — light bouncing off standing water.
  for (const [tx, ty, rot, rx2, ry2, a] of [
    [58, 60, -0.6, 34, 8, 0.26],
    [74, 78, -0.5, 18, 4.5, 0.18],
  ] as const) {
    x.save();
    x.translate(tx, ty);
    x.rotate(rot);
    const g = x.createLinearGradient(0, -ry2, 0, ry2);
    g.addColorStop(0, 'rgba(196, 222, 244, 0)');
    g.addColorStop(0.5, `rgba(196, 222, 244, ${a})`);
    g.addColorStop(1, 'rgba(196, 222, 244, 0)');
    x.fillStyle = g;
    x.beginPath();
    x.ellipse(0, 0, rx2, ry2, 0, 0, Math.PI * 2);
    x.fill();
    x.restore();
  }
  featherEdge(x, 128, 0.82);
  return canvasTex(c);
}

/** Hex → css rgba, for the canvas painters. */
function rgba(hex: number, a: number): string {
  return `rgba(${(hex >> 16) & 255}, ${(hex >> 8) & 255}, ${hex & 255}, ${a})`;
}

/**
 * The REFLECTED SIGNS painted into a puddle (emissiveMap).
 *
 * A city's neon lives in emissive scene geometry, and an environment map
 * carries only the SKY — so no amount of roughness/env tuning can ever put
 * Shibuya's signs in a puddle. Reflection had to be authored: soft columns of
 * the city's own two accent hues, BROKEN into ripple bands (an unbroken
 * column reads as a painted stripe; the horizontal break is what says
 * "water"), clipped just inside the puddle rim so the glow can never spill
 * onto dry tarmac. Black elsewhere = zero emissive elsewhere.
 *
 * Deterministic: every jitter comes from `art()`, never the layout rng.
 */
function puddleGlowTexture(hueA: number, hueB: number): THREE.CanvasTexture {
  const [c, x] = makeCanvas(256, 256);
  x.fillStyle = '#000';
  x.fillRect(0, 0, 256, 256);
  x.scale(2, 2); // draw in 128-space, exactly like puddleTexture
  x.save();
  blobPath(x, 64, 64, 52, 3); // just INSIDE the puddle's own rim
  x.clip();
  // Four sign columns + a pale sky column, running ALONG the course (canvas y)
  // because that is the axis a standing reflection smears down. Each column is
  // a continuous smear FIRST, then broken by ripple bands on top: bands alone
  // read as coloured confetti (measured), the smear is what makes them one
  // reflected sign.
  const cols: [number, number, number, number][] = [
    // [centre x, half width, colour, peak alpha]
    [32, 9, hueA, 0.46],
    [52, 6, hueB, 0.4],
    [74, 11, hueB, 0.5],
    [94, 7, hueA, 0.38],
    [62, 16, 0xbcd2e6, 0.13], // the sky/street-lamp catch
  ];
  const smear = (cx: number, w: number, col: number, a: number, y: number, h: number): void => {
    const g = x.createLinearGradient(cx - w, 0, cx + w, 0);
    g.addColorStop(0, rgba(col, 0));
    g.addColorStop(0.35, rgba(col, a * 0.55));
    g.addColorStop(0.5, rgba(col, a));
    g.addColorStop(0.65, rgba(col, a * 0.55));
    g.addColorStop(1, rgba(col, 0));
    x.fillStyle = g;
    x.fillRect(cx - w, y, w * 2, h);
  };
  cols.forEach(([cx, halfW, col, peak], ci) => {
    // The unbroken smear — a wobbling column down the puddle.
    for (let k = 0; k < 26; k++) {
      const y = 6 + k * 4.4;
      const wob = (art(ci * 41 + k) - 0.5) * halfW * 0.5;
      const fade = 1 - k / 30;
      smear(cx + wob, halfW * (0.75 + art(ci * 23 + k) * 0.3), col, peak * 0.3 * fade, y, 4.6);
    }
    // …broken by brighter ripple crests riding across it.
    for (let k = 0; k < 13; k++) {
      const y = 8 + k * 8.6 + (art(ci * 31 + k * 3 + 1) - 0.5) * 4;
      const band = 2.2 + art(ci * 17 + k) * 3.4;
      const w = halfW * (0.6 + art(ci * 13 + k * 5) * 0.7);
      const a = peak * (1 - k / 16) * (0.5 + art(ci * 7 + k * 11) * 0.5);
      if (a <= 0.01) continue;
      smear(cx + (art(ci * 19 + k) - 0.5) * halfW * 0.4, w, col, a, y, band);
    }
  });
  x.restore();
  return canvasTex(c);
}

/** Pale-blue ice sheet: crystalline cracks, a FROST FRINGE round the rim and
 *  a cold sheen — a 0.32-grip trap must be nameable at speed (GS4b: it was
 *  literally invisible on Patagonia's gravel before the layering fix). */
function iceTexture(): THREE.CanvasTexture {
  const [c, x] = makeCanvas(256, 256);
  x.scale(2, 2);
  blobPath(x, 64, 64, 60, 17);
  x.fillStyle = 'rgba(206, 232, 250, 0.88)';
  x.fill();
  blobPath(x, 64, 64, 42, 17);
  x.fillStyle = 'rgba(226, 243, 255, 0.6)';
  x.fill();
  // FROST FRINGE: a soft white rim just inside the edge (rime creeping in
  // from the shoulder) — the cue that survives flat Low-quality lighting.
  x.save();
  blobPath(x, 64, 64, 60, 17);
  x.clip();
  blobPath(x, 64, 64, 57, 17);
  x.lineWidth = 8;
  x.strokeStyle = 'rgba(255, 255, 255, 0.5)';
  x.stroke();
  // Cracks clipped to the sheet — kinked segments read as fractures (dead
  // straight lines just read as scratches on the road).
  x.lineCap = 'round';
  x.strokeStyle = 'rgba(255, 255, 255, 0.9)';
  x.lineWidth = 1.6;
  for (const [ox, oy, n, seed] of [
    [52, 58, 7, 40],
    [82, 76, 6, 60],
  ] as const) {
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + art(seed + i) * 0.9;
      let px = ox;
      let py = oy;
      x.beginPath();
      x.moveTo(px, py);
      for (let k = 1; k <= 4; k++) {
        const len = 8 + art(seed + i * 3 + k) * 13;
        const wob = (art(seed + i * 7 + k) - 0.5) * 1.1;
        px += Math.cos(a + wob) * len;
        py += Math.sin(a + wob) * len;
        x.lineTo(px, py);
        // Hairline side-branches off the main fracture.
        if (k === 2 && art(seed + i * 11) > 0.5) {
          x.moveTo(px, py);
          x.lineTo(px + Math.cos(a + 1.1) * 7, py + Math.sin(a + 1.1) * 7);
          x.moveTo(px, py);
        }
      }
      x.stroke();
    }
  }
  x.restore();
  featherEdge(x, 128, 0.86);
  return canvasTex(c);
}

/** Tan drift with darker wind-ripple wave lines (the flat ground skirt — the
 *  3D heap on top comes from the dome in slickMesh). */
function sandTexture(): THREE.CanvasTexture {
  const [c, x] = makeCanvas(256, 256);
  x.scale(2, 2);
  blobPath(x, 64, 64, 62, 71);
  x.fillStyle = '#d8b070';
  x.fill();
  x.save();
  blobPath(x, 64, 64, 62, 71);
  x.clip();
  // Repeated darker waves = "wind-blown sand", not "brown paint".
  x.strokeStyle = 'rgba(168, 128, 72, 0.6)';
  x.lineWidth = 3;
  for (let row = 0; row < 7; row++) {
    const y0 = 12 + row * 17 + art(80 + row) * 6;
    x.beginPath();
    for (let px = -4; px <= 132; px += 6) {
      const py = y0 + Math.sin(px * 0.09 + row * 1.7) * 4;
      if (px < 0) x.moveTo(px, py);
      else x.lineTo(px, py);
    }
    x.stroke();
  }
  // Grain speckle so the drift doesn't read as smooth paint.
  for (let i = 0; i < 420; i++) {
    x.fillStyle = art(i * 3) < 0.5 ? 'rgba(120, 88, 44, 0.35)' : 'rgba(244, 224, 180, 0.4)';
    x.fillRect(art(i * 5 + 1) * 128, art(i * 7 + 2) * 128, 0.7, 0.7);
  }
  x.restore();
  featherEdge(x, 128, 0.8);
  return canvasTex(c);
}

/** Soft-edged white mist blobs for the ground patch (radial gradients). */
function steamTexture(): THREE.CanvasTexture {
  const [c, x] = makeCanvas(128, 128);
  for (let i = 0; i < 9; i++) {
    const bx = 22 + art(90 + i) * 84;
    const by = 22 + art(99 + i) * 84;
    const br = 16 + art(108 + i) * 22;
    const g = x.createRadialGradient(bx, by, 0, bx, by, br);
    g.addColorStop(0, 'rgba(255, 255, 255, 0.5)');
    g.addColorStop(1, 'rgba(255, 255, 255, 0)');
    x.fillStyle = g;
    x.fillRect(bx - br, by - br, br * 2, br * 2);
  }
  return canvasTex(c);
}

/** Vertically-LOOPABLE mist puffs (drawn wrapped) — this is the layer that
 *  SCROLLS via tickFeatures so the steam visibly rises. The static edge fade
 *  lives in mistFadeTexture (the alphaMap keeps its own un-scrolled UVs). */
function mistScrollTexture(): THREE.CanvasTexture {
  const [c, x] = makeCanvas(64, 128);
  for (let i = 0; i < 9; i++) {
    const bx = 8 + art(120 + i) * 48;
    const by = art(131 + i) * 128;
    const br = 10 + art(140 + i) * 13;
    const g0 = 0.35 + art(150 + i) * 0.3;
    // Draw each puff at y, y−128 and y+128 so the tile wraps seamlessly.
    for (const oy of [-128, 0, 128]) {
      const g = x.createRadialGradient(bx, by + oy, 0, bx, by + oy, br);
      g.addColorStop(0, `rgba(255, 255, 255, ${g0})`);
      g.addColorStop(1, 'rgba(255, 255, 255, 0)');
      x.fillStyle = g;
      x.fillRect(bx - br, by + oy - br, br * 2, br * 2);
    }
  }
  const tex = canvasTex(c);
  tex.wrapT = THREE.RepeatWrapping;
  return tex;
}

/** Static alpha shaping for the mist sheets (alphaMap reads the GREEN
 *  channel): gone at the top, solid at the base, faded at both side edges —
 *  hides the plane's rectangle while the map layer scrolls independently
 *  (each texture keeps its own UV transform, so the fade never scrolls). */
function mistFadeTexture(): THREE.CanvasTexture {
  const [c, x] = makeCanvas(64, 128);
  const v = x.createLinearGradient(0, 0, 0, 128);
  v.addColorStop(0, '#000000'); // canvas top = sheet top (flipY) → alpha 0
  v.addColorStop(0.5, '#8a8a8a');
  v.addColorStop(1, '#ffffff');
  x.fillStyle = v;
  x.fillRect(0, 0, 64, 128);
  const hg = x.createLinearGradient(0, 0, 64, 0);
  hg.addColorStop(0, '#000000');
  hg.addColorStop(0.28, '#ffffff');
  hg.addColorStop(0.72, '#ffffff');
  hg.addColorStop(1, '#000000');
  x.globalCompositeOperation = 'multiply';
  x.fillStyle = hg;
  x.fillRect(0, 0, 64, 128);
  x.globalCompositeOperation = 'source-over';
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.NoColorSpace; // alpha data, not colour
  return tex;
}

/** Sewer-grate slats for the steam vent's street plate. */
function grateTexture(): THREE.CanvasTexture {
  const [c, x] = makeCanvas(128, 128);
  x.fillStyle = '#15171c';
  x.fillRect(0, 0, 128, 128);
  for (let row = 0; row < 7; row++) {
    const y0 = 10 + row * 17;
    x.fillStyle = '#000000';
    x.fillRect(12, y0, 104, 9); // slot
    x.fillStyle = '#2e333c';
    x.fillRect(12, y0 - 2, 104, 2); // bar catch-light
  }
  x.strokeStyle = '#31363e';
  x.lineWidth = 5;
  x.strokeRect(4, 4, 120, 120);
  return canvasTex(c);
}

/** Wet asphalt, per city: the shared dark blob map plus a REFLECTION painted
 *  in that city's own two accent hues (see puddleGlowTexture). Keyed so every
 *  puddle in a city shares one material, and each city gets its own. */
let puddleMap: THREE.CanvasTexture | null = null;
let puddleMats: Map<string, THREE.MeshStandardMaterial> | null = null;

function puddleMatFor(theme: TrackTheme): THREE.MeshStandardMaterial {
  // Sign reflections only read once the sun is off them: night cities (Tokyo /
  // London, sun 1.5) get the full effect, noon cities (Lagos 2.7, Mumbai 2.3)
  // a whisper — the inverse of the boost pad's dayBoost, same principle.
  const night = Math.min(1, Math.max(0.22, (2.65 - theme.sunIntensity) / 1.15));
  const key = `${theme.roadLine}:${theme.wall}:${night.toFixed(2)}`;
  puddleMats ??= new Map();
  let m = puddleMats.get(key);
  if (!m) {
    puddleMap ??= puddleTexture();
    m = new THREE.MeshStandardMaterial({
      map: puddleMap,
      transparent: true,
      depthWrite: false,
      roughness: 0.03,
      metalness: 0,
      // Standing water is a near-mirror: push the (sky-only) env reflection
      // hard, then let the painted signs carry the city's own light.
      envMapIntensity: 3.2,
      emissive: 0xffffff,
      emissiveMap: puddleGlowTexture(theme.roadLine, theme.wall),
      emissiveIntensity: 1.15 * night,
    });
    puddleMats.set(key, m);
  }
  return m;
}

/** One shared material per slick style — built once, reused by every patch. */
interface SlickKit {
  ice: THREE.MeshStandardMaterial;
  sand: THREE.MeshStandardMaterial;
  sandDome: THREE.MeshStandardMaterial;
  steam: THREE.MeshBasicMaterial;
  grate: THREE.MeshStandardMaterial;
  mist: THREE.MeshBasicMaterial;
}

let slickKit: SlickKit | null = null;
/** The scrolling mist map — module-level so tickFeatures can advance it. */
let mistScroll: THREE.CanvasTexture | null = null;

function makeSlickKit(): SlickKit {
  mistScroll = mistScrollTexture();
  return {
    // Ice stays MeshStandard so it mirrors each city's sky (the IBL env) — the
    // reflection is what says "frozen"; the canvas gives the identifiable
    // SHAPE. (Puddles are per-city now: see puddleMatFor.)
    ice: new THREE.MeshStandardMaterial({
      map: iceTexture(),
      transparent: true,
      depthWrite: false,
      roughness: 0.07,
      metalness: 0,
      envMapIntensity: 1.9,
    }),
    sand: new THREE.MeshStandardMaterial({
      map: sandTexture(),
      transparent: true,
      depthWrite: false,
      roughness: 0.95,
      metalness: 0,
    }),
    sandDome: new THREE.MeshStandardMaterial({
      color: 0xdcb474,
      map: groundTexture('sand'),
      roughness: 0.96,
      metalness: 0,
    }),
    // Steam is self-lit (Basic) so the mist stays visible on night courses;
    // the grey tint keeps it under the bloom threshold.
    steam: new THREE.MeshBasicMaterial({
      map: steamTexture(),
      transparent: true,
      depthWrite: false,
      color: 0xd8e2ea,
    }),
    grate: new THREE.MeshStandardMaterial({
      map: grateTexture(),
      roughness: 0.62,
      metalness: 0.45,
    }),
    mist: new THREE.MeshBasicMaterial({
      map: mistScroll,
      alphaMap: mistFadeTexture(),
      transparent: true,
      depthWrite: false,
      color: 0xcfd8e2,
      opacity: 0.85,
      side: THREE.DoubleSide,
    }),
  };
}

/**
 * nyc-lake — THE BOW BRIDGE FORD. The Lake runs ACROSS the road under the span
 * and a make-shift PLANK CROSSING carries the racing line over it (owner spec;
 * physics in trackFeatures.ts `inFordWater`).
 *
 * Everything here is DISPLAY ONLY. The plank is a lateral exemption WINDOW in
 * the sim, not a raised deck, so the boards are laid flush on the road — a real
 * raised deck would inherit the whole ramp/channel clearance problem (an edge
 * to catch, a lip to launch off, a soffit for a ball to lodge under) to buy a
 * few centimetres of silhouette. Boards run ACROSS the run, the Lagos jetty
 * convention, so the tyres cross them like a cattle grid.
 *
 * THE WATER IS THE SAME MATERIAL as the two lake lobes it joins
 * (builders.courseWaterMaterial — shared ripple normals, shared scrolling
 * uTime, shared world-anchored UVs): the whole point is that the driver reads
 * ONE body of water with a road cut through it, and a bespoke sheet here is how
 * you end up with two lakes and a puddle.
 */
function fordMesh(f: PlacedFeature, slopePitch: number, city: City): THREE.Group {
  const root = featureRoot(f, 0, slopePitch);
  const w = f.halfWidth * 2;
  const l = f.halfLength * 2;
  const lakeColor = city.water?.lakes?.[0]?.color ?? 0x2f5f74;

  // --- the water sheet ------------------------------------------------------
  // GS4b layering law (see MARKINGS_CLEAR): it must clear the road-paint ribbon
  // and draw after it, or the city's centreline paints straight across the ford.
  const water = new THREE.Mesh(new THREE.PlaneGeometry(w, l, 6, 6), courseWaterMaterial(city.water));
  const wm = water.material as THREE.MeshStandardMaterial;
  wm.color.setHex(lakeColor);
  // MEASURED: The Lake's own 0.14 roughness is a near-mirror, and a near-mirror
  // seen at the chase camera's ~5° depression is 100% sky — the ford rendered
  // as a flat pale sheet with no colour at all. A shallow crossing over tarmac
  // is not open water anyway; 0.34 lets its blue through at every range while
  // keeping the ripple highlights.
  wm.roughness = 0.34;
  water.rotation.x = -Math.PI / 2;
  water.position.y = MARKINGS_CLEAR + 0.01;
  water.renderOrder = DECAL_ORDER;
  root.add(water);

  // --- wet mud margins where the water meets the dry road -------------------
  // A hard water/tarmac edge reads as a decal. Two darkened damp bands at the
  // up- and down-course lips give it a bank.
  const wetMat = new THREE.MeshStandardMaterial({
    color: 0x3a3730,
    roughness: 0.42,
    transparent: true,
    opacity: 0.75,
  });
  // …and a bright FOAM line right on each waterline. This is the cue that
  // survives distance: MEASURED from 62 u back (chase-camera height), the water
  // sheet itself subtends ~2° and vanishes into the tarmac, while a hard pale
  // line straight across the road reads instantly as "water ahead" — which is
  // what makes a driver aim at the plank instead of discovering the ford by
  // driving into it.
  const foamMat = new THREE.MeshStandardMaterial({
    color: 0xd6e2e6,
    roughness: 0.7,
    transparent: true,
    opacity: 0.72,
  });
  for (const s of [-1, 1] as const) {
    const wet = new THREE.Mesh(new THREE.PlaneGeometry(w, 3.2), wetMat);
    wet.rotation.x = -Math.PI / 2;
    wet.position.set(0, MARKINGS_CLEAR + 0.005, s * (f.halfLength + 1.4));
    wet.renderOrder = DECAL_ORDER;
    root.add(wet);
    const foam = new THREE.Mesh(new THREE.PlaneGeometry(w, 0.9), foamMat);
    foam.rotation.x = -Math.PI / 2;
    foam.position.set(0, MARKINGS_CLEAR + 0.02, s * (f.halfLength - 0.2));
    foam.renderOrder = DECAL_ORDER + 1;
    root.add(foam);
  }

  // --- the plank crossing ---------------------------------------------------
  const deckW = f.plankHalfWidth * 2;
  const boards = plankTexture();
  // MEASURED on screen: the repeat goes on V, not U. plankTexture draws its
  // board seams as horizontal canvas rows, i.e. along u — and on a box's top
  // face u is LATERAL, so the seams already run across the run. Repeating on U
  // (the first thing I tried) just squeezed 9 copies across the width and the
  // deck rendered as a plain brown slab. Repeating on V lays one board course
  // per ~1.6 u of run, which is what reads as planking from the driver's seat.
  boards.repeat.set(1, Math.max(2, Math.round((l + 2.6) / 1.6)));
  const boardMat = new THREE.MeshStandardMaterial({
    map: boards,
    color: 0x8e8071, // knocks the raw texture's orange back to weathered timber
    roughness: 0.96,
  });
  const deck = new THREE.Mesh(new THREE.BoxGeometry(deckW, 0.16, l + 2.6), boardMat);
  deck.position.y = MARKINGS_CLEAR + 0.09;
  root.add(deck);
  // Stringer beams under each edge — the thing that makes it read as BUILT
  // rather than painted, and what a driver sees first at a grazing angle.
  const beamMat = new THREE.MeshStandardMaterial({ color: 0x6b543a, roughness: 1 });
  for (const s of [-1, 1] as const) {
    const beam = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.3, l + 2.6), beamMat);
    beam.position.set(s * (f.plankHalfWidth - 0.2), MARKINGS_CLEAR + 0.02, 0);
    root.add(beam);
    // Low kerb rails so the dry lane is unmistakable from up the road (they sit
    // ON the deck, well inboard of the jumpable width — scenery, never a wall).
    const rail = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.34, l + 2.6), beamMat);
    rail.position.set(s * (f.plankHalfWidth - 0.17), MARKINGS_CLEAR + 0.33, 0);
    root.add(rail);
  }
  // Trestles in the water at both lips: four short posts each, the make-shift
  // read the owner asked for (scaffold poles under a plank, not a bridge).
  const postMat = new THREE.MeshStandardMaterial({ color: 0x5a4830, roughness: 1 });
  const posts = new THREE.InstancedMesh(new THREE.CylinderGeometry(0.16, 0.18, 1.1, 6), postMat, 8);
  const pm = new THREE.Matrix4();
  const pp = new THREE.Vector3();
  const pq = new THREE.Quaternion();
  const ps = new THREE.Vector3(1, 1, 1);
  let n = 0;
  for (const along of [-1, 1] as const) {
    for (const lat of [-1, 1] as const) {
      pp.set(lat * (f.plankHalfWidth - 0.3), MARKINGS_CLEAR - 0.35, along * (f.halfLength - 1.2));
      posts.setMatrixAt(n++, pm.compose(pp, pq, ps));
      pp.set(lat * (f.plankHalfWidth - 0.3), MARKINGS_CLEAR - 0.35, along * (f.halfLength * 0.4));
      posts.setMatrixAt(n++, pm.compose(pp, pq, ps));
    }
  }
  posts.instanceMatrix.needsUpdate = true;
  root.add(posts);
  return root;
}

function slickMesh(f: PlacedFeature, slopePitch: number, theme: TrackTheme): THREE.Group {
  const root = featureRoot(f, 0, slopePitch);
  const kit = (slickKit ??= makeSlickKit());
  const key: 'puddle' | 'ice' | 'sand' | 'steam' =
    f.style === 'ice' || f.style === 'sand' || f.style === 'steam' ? f.style : 'puddle';
  const mat = key === 'puddle' ? puddleMatFor(theme) : kit[key];
  const m = new THREE.Mesh(new THREE.PlaneGeometry(f.halfWidth * 2, f.halfLength * 2), mat);
  m.rotation.x = -Math.PI / 2;
  // GS4b layering law: the decal must clear the road-paint/gravel ribbon AND
  // draw after it in the transparent pass (see MARKINGS_CLEAR — this line is
  // the Patagonia-ice fix).
  m.position.y = MARKINGS_CLEAR + 0.005;
  m.renderOrder = DECAL_ORDER;
  root.add(m);
  if (key === 'sand') {
    // A low heaped dome ON the drift — the hazard reads in 3D from the
    // approach instead of vanishing at a grazing camera angle (~20 tris).
    const dome = new THREE.Mesh(
      new THREE.SphereGeometry(1, 9, 4, 0, Math.PI * 2, 0, Math.PI / 2.15),
      kit.sandDome,
    );
    dome.scale.set(f.halfWidth * 0.68, 0.55, f.halfLength * 0.5);
    dome.position.y = 0.02;
    root.add(dome);
  }
  if (key === 'steam') {
    // A dark street grate under the vent — the mist now comes FROM something.
    const grate = new THREE.Mesh(
      new THREE.BoxGeometry(Math.min(3.4, f.halfWidth * 1.1), 0.09, Math.min(4.6, f.halfLength)),
      kit.grate,
    );
    grate.position.y = MARKINGS_CLEAR + 0.045;
    root.add(grate);
    // Three crossed upright mist sheets, their puff layer SCROLLING upward
    // via tickFeatures — a flat decal alone disappears at a shallow angle,
    // and static sheets read as frosted glass.
    const w = Math.max(2.6, f.halfWidth * 1.3);
    const h = 2.9;
    for (const [a, ox] of [
      [Math.PI / 4, 0],
      [-Math.PI / 4, 0],
      [Math.PI / 2, 0.4],
    ] as const) {
      const sheet = new THREE.Mesh(new THREE.PlaneGeometry(w, h), kit.mist);
      sheet.rotation.y = a;
      sheet.position.set(ox, h / 2, 0);
      root.add(sheet);
    }
  }
  return root;
}

// -- Obstacles: shared prop kit + per-style detail so each hazard is nameable
//    at a glance (cab, boulder, crate stack, works barrier, cone, Vespas). ----

/** Wooden crate face: vertical planks with gap shadows + a darker batten
 *  frame. GS4b: 128px with real grain runs + knots (the 64px version blurred
 *  into flat brown at chase distance). */
function crateTexture(): THREE.CanvasTexture {
  const [c, x] = makeCanvas(128, 128);
  x.scale(2, 2);
  x.fillStyle = '#b0854e';
  x.fillRect(0, 0, 64, 64);
  for (let p = 0; p < 4; p++) {
    const px = p * 16;
    x.fillStyle = p % 2 ? '#a97e49' : '#b58a52'; // alternating plank tone
    x.fillRect(px + 1, 0, 14, 64);
    x.fillStyle = 'rgba(58, 38, 18, 0.8)'; // plank-gap shadow
    x.fillRect(px, 0, 2, 64);
    // Long wavering grain strokes per plank + the odd knot.
    x.strokeStyle = 'rgba(90, 62, 30, 0.4)';
    x.lineWidth = 0.7;
    for (let gl = 0; gl < 5; gl++) {
      const gx = px + 3 + art(p * 5 + gl) * 10;
      x.beginPath();
      x.moveTo(gx, 2);
      for (let seg = 1; seg <= 4; seg++) {
        x.lineTo(gx + (art(p * 7 + gl * 3 + seg) - 0.5) * 2.4, seg * 16);
      }
      x.stroke();
    }
    if (art(p * 13 + 5) > 0.55) {
      const kx = px + 4 + art(p * 17) * 8;
      const ky = 10 + art(p * 19) * 44;
      x.fillStyle = 'rgba(74, 48, 22, 0.85)';
      x.beginPath();
      x.ellipse(kx, ky, 1.6, 2.4, 0.3, 0, Math.PI * 2);
      x.fill();
    }
  }
  x.strokeStyle = '#7d5a32';
  x.lineWidth = 7;
  x.strokeRect(3.5, 3.5, 57, 57);
  // Batten nail heads at the corners.
  x.fillStyle = '#3a2812';
  for (const [nx, ny] of [
    [6, 6],
    [58, 6],
    [6, 58],
    [58, 58],
  ] as const) {
    x.beginPath();
    x.arc(nx, ny, 1.1, 0, Math.PI * 2);
    x.fill();
  }
  return canvasTex(c);
}

/** NYC-cab checker band: two rows of black/white checkers. */
function checkerTexture(): THREE.CanvasTexture {
  const [c, x] = makeCanvas(64, 16);
  x.fillStyle = '#f5f2e8';
  x.fillRect(0, 0, 64, 16);
  x.fillStyle = '#15161a';
  for (let col = 0; col < 8; col++)
    for (let row = 0; row < 2; row++) if ((col + row) % 2 === 0) x.fillRect(col * 8, row * 8, 8, 8);
  return canvasTex(c);
}

/** Mottled granite (M58): speckle patches over a grey base + a few dark
 *  fracture strokes — tints per-material so boulder palettes still vary. */
function rockTexture(): THREE.CanvasTexture {
  const [c, x] = makeCanvas(64, 64);
  x.fillStyle = '#7d8085';
  x.fillRect(0, 0, 64, 64);
  for (let i = 0; i < 90; i++) {
    const g = 96 + Math.floor(art(i) * 80);
    x.fillStyle = `rgba(${g},${g + 2},${g + 6},0.5)`;
    x.beginPath();
    x.arc(art(i * 3 + 1) * 64, art(i * 5 + 2) * 64, 2 + art(i * 7 + 3) * 7, 0, Math.PI * 2);
    x.fill();
  }
  x.strokeStyle = 'rgba(40,42,46,0.55)';
  x.lineWidth = 1;
  for (let i = 0; i < 7; i++) {
    x.beginPath();
    x.moveTo(art(i * 11 + 4) * 64, art(i * 13 + 5) * 64);
    x.lineTo(art(i * 17 + 6) * 64, art(i * 19 + 7) * 64);
    x.stroke();
  }
  return canvasTex(c);
}

/** Roadworks red/white DIAGONAL stripes (M58) for the barrier board, with a
 *  little grime so it reads "street furniture", not "fresh sticker". */
function stripeTexture(): THREE.CanvasTexture {
  const [c, x] = makeCanvas(128, 32);
  x.fillStyle = '#f4f1ea';
  x.fillRect(0, 0, 128, 32);
  x.fillStyle = '#d23333';
  for (let i = -1; i < 5; i++) {
    const x0 = i * 32;
    x.beginPath();
    x.moveTo(x0, 32);
    x.lineTo(x0 + 16, 0);
    x.lineTo(x0 + 32, 0);
    x.lineTo(x0 + 16, 32);
    x.closePath();
    x.fill();
  }
  for (let i = 0; i < 26; i++) {
    x.fillStyle = `rgba(60,50,40,${0.1 + art(i * 3) * 0.15})`;
    x.fillRect(art(i * 5 + 1) * 128, art(i * 7 + 2) * 32, 2 + art(i * 11) * 3, 1.5);
  }
  return canvasTex(c);
}

/** Traffic-cone skin (M58): hot orange fading dirtier toward the base, the
 *  white reflective band with a sheen line baked in, grime flecks below. */
function coneTexture(): THREE.CanvasTexture {
  const [c, x] = makeCanvas(64, 64);
  const g = x.createLinearGradient(0, 0, 0, 64);
  g.addColorStop(0, '#ff8a2a');
  g.addColorStop(0.72, '#f26f16');
  g.addColorStop(1, '#bf5511');
  x.fillStyle = g;
  x.fillRect(0, 0, 64, 64);
  x.fillStyle = '#efeeea';
  x.fillRect(0, 26, 64, 12);
  x.fillStyle = 'rgba(255,255,255,0.85)';
  x.fillRect(0, 28, 64, 3);
  for (let i = 0; i < 22; i++) {
    x.fillStyle = `rgba(70,40,20,${0.15 + art(i * 3) * 0.2})`;
    x.fillRect(art(i * 5) * 64, 46 + art(i * 7) * 17, 2, 2);
  }
  return canvasTex(c);
}

/** A lumpy displaced boulder (M58 — real rock silhouettes instead of the old
 *  clean icosahedron). The displacement is keyed on vertex POSITION, not
 *  index: flat-shading duplicates corner vertices, and a position hash moves
 *  all copies of a corner together so the faces stay sealed. Deterministic
 *  per seed — never touches the layout RNG. */
function rockGeometry(radius: number, seed: number): THREE.BufferGeometry {
  const geo = new THREE.IcosahedronGeometry(radius, 1);
  const pos = geo.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const z = pos.getZ(i);
    const h = art(seed * 7.3 + x * 12.9898 + y * 78.233 + z * 37.719);
    const k = 0.78 + h * 0.44;
    pos.setXYZ(i, x * k, y * k * 0.82, z * k); // squashed a touch: sits, not floats
  }
  geo.computeVertexNormals();
  return geo;
}

/** M158: a broken BOULDER for the rock obstacles. Seeded fracture planes cut
 *  flat broken faces into it and a few slow waves give it its lumps; the
 *  finer mesh lets the flat shading read as stone grain. Everything is a
 *  function of the vertex POSITION, so the duplicated corners of the
 *  non-indexed mesh move together and the faces stay sealed. */
function boulderGeometry(radius: number, seed: number, detail: number): THREE.BufferGeometry {
  const geo = new THREE.IcosahedronGeometry(1, detail);
  const pos = geo.attributes.position as THREE.BufferAttribute;
  const planes: { x: number; y: number; z: number; d: number }[] = [];
  for (let i = 0; i < 9; i++) {
    const u = art(seed * 3.1 + i * 7.7) * 2 - 1;
    const a = art(seed * 5.3 + i * 3.9) * Math.PI * 2;
    const s = Math.sqrt(1 - u * u);
    planes.push({ x: s * Math.cos(a), y: u, z: s * Math.sin(a), d: 0.6 + art(seed * 1.7 + i * 2.3) * 0.22 });
  }
  const waves: { x: number; y: number; z: number; f: number; a: number; p: number }[] = [];
  for (let i = 0; i < 6; i++) {
    const u = art(seed * 2.7 + i * 5.1) * 2 - 1;
    const t = art(seed * 4.9 + i * 6.7) * Math.PI * 2;
    const s = Math.sqrt(1 - u * u);
    const big = i < 3;
    waves.push({
      x: s * Math.cos(t),
      y: u,
      z: s * Math.sin(t),
      f: big ? 1.8 + art(seed + i) * 0.8 : 4.5 + art(seed + i * 9) * 2,
      a: big ? 0.1 : 0.035,
      p: art(seed * 6.1 + i) * Math.PI * 2,
    });
  }
  const v = new THREE.Vector3();
  for (let i = 0; i < pos.count; i++) {
    v.fromBufferAttribute(pos, i);
    let r = 1;
    for (const w of waves) r += w.a * Math.sin((v.x * w.x + v.y * w.y + v.z * w.z) * w.f + w.p);
    v.multiplyScalar(r);
    for (const p of planes) {
      const s = v.x * p.x + v.y * p.y + v.z * p.z;
      if (s > p.d) v.set(v.x - p.x * (s - p.d), v.y - p.y * (s - p.d), v.z - p.z * (s - p.d));
    }
    pos.setXYZ(i, v.x * radius, v.y * radius * 0.82, v.z * radius); // squashed a touch: sits, not floats
  }
  geo.computeVertexNormals();
  return geo;
}

/** Extrude a SIDE PROFILE (x = along the car, +front; y = up) across a width —
 *  the same trick the racers' CarView uses, so the parked taxi gets a real
 *  hood → windshield → roof → trunk sedan silhouette. Profile x maps to +Z. */
function extrudeSide(shape: THREE.Shape, width: number): THREE.ExtrudeGeometry {
  const geo = new THREE.ExtrudeGeometry(shape, { depth: width, bevelEnabled: false });
  geo.rotateY(-Math.PI / 2);
  geo.translate(width / 2, 0, 0);
  return geo;
}

/** The parked NYC cab's body silhouette (length 3.4 — the old box footprint). */
function taxiBodyProfile(): THREE.Shape {
  const s = new THREE.Shape();
  s.moveTo(1.7, 0.3); // front bumper base
  s.lineTo(1.72, 0.62); // grille
  s.lineTo(1.35, 0.72); // hood front
  s.lineTo(0.55, 0.78); // hood at the windshield base
  s.lineTo(0.12, 1.32); // raked windshield
  s.lineTo(-0.75, 1.34); // roof
  s.lineTo(-1.25, 0.86); // rear-glass slope
  s.lineTo(-1.68, 0.8); // trunk
  s.lineTo(-1.7, 0.3); // tail
  s.lineTo(1.7, 0.3);
  return s;
}

/** The cab's glasshouse (slightly inset from the body). */
function taxiCabinProfile(): THREE.Shape {
  const s = new THREE.Shape();
  s.moveTo(0.5, 0.8);
  s.lineTo(0.07, 1.28);
  s.lineTo(-0.7, 1.3);
  s.lineTo(-1.16, 0.86);
  s.lineTo(0.5, 0.8);
  return s;
}

/** Soft radial contact shadow (M99): a cheap gradient disc that GROUNDS every
 *  prop — without it the flat-lit props read as pasted on the road.
 *  `strength` scales the core darkness (crates get the heavier variant). */
function contactShadowTexture(strength = 0.4): THREE.CanvasTexture {
  const [c, x] = makeCanvas(64, 64);
  const g = x.createRadialGradient(32, 32, 4, 32, 32, 30);
  g.addColorStop(0, `rgba(0,0,0,${strength})`);
  g.addColorStop(0.7, `rgba(0,0,0,${strength * 0.6})`);
  g.addColorStop(1, 'rgba(0,0,0,0)');
  x.fillStyle = g;
  x.fillRect(0, 0, 64, 64);
  return canvasTex(c);
}

/** The parked cab's side glazing: door panes + B-pillar + a sky-catch sweep,
 *  applied as a thin band flush to the glasshouse flank (the extruded cabin's
 *  side face is otherwise one blank dark sheet). */
function glassBandTexture(): THREE.CanvasTexture {
  const [c, x] = makeCanvas(128, 32);
  x.fillStyle = '#10151c';
  x.fillRect(0, 0, 128, 32);
  // Three panes: front door / rear door / rear quarter, chrome mullions.
  const panes: readonly [number, number][] = [
    [6, 40],
    [52, 40],
    [98, 24],
  ];
  for (const [px, pw] of panes) {
    x.fillStyle = '#232f3d';
    x.fillRect(px, 4, pw, 24);
    // Sky-catch diagonal.
    x.fillStyle = 'rgba(178, 208, 232, 0.3)';
    x.beginPath();
    x.moveTo(px, 4);
    x.lineTo(px + pw * 0.55, 4);
    x.lineTo(px, 28);
    x.closePath();
    x.fill();
  }
  x.fillStyle = '#8a919c'; // brightwork mullions
  for (const mx of [4, 48, 50, 94, 96, 124]) x.fillRect(mx, 4, 2, 24);
  return canvasTex(c);
}

/** Blotchy weathering roughness map — breaks the cab's uniform clearcoat into
 *  waxed/dulled patches so the paint reads street-worn, not toy-plastic. */
function weatherRoughTexture(): THREE.CanvasTexture {
  const [c, x] = makeCanvas(64, 64);
  x.fillStyle = '#8c8c8c'; // base roughness ~0.55
  x.fillRect(0, 0, 64, 64);
  for (let i = 0; i < 26; i++) {
    const bx = art(i * 5 + 200) * 64;
    const by = art(i * 7 + 210) * 64;
    const br = 4 + art(i * 11 + 220) * 12;
    const glossy = art(i * 13 + 230) > 0.5;
    const g = x.createRadialGradient(bx, by, 0, bx, by, br);
    g.addColorStop(0, glossy ? 'rgba(70,70,70,0.55)' : 'rgba(190,190,190,0.5)');
    g.addColorStop(1, 'rgba(0,0,0,0)');
    x.fillStyle = g;
    x.fillRect(bx - br, by - br, br * 2, br * 2);
  }
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.NoColorSpace;
  return tex;
}

/** Everything the obstacle props share — built ONCE and reused by every
 *  instance in every city (the same lazy-singleton pattern as `chevrons`).
 *  Shared materials + the small repeated geometries (wheels, lamps). */
interface ObstacleKit {
  cab: THREE.MeshStandardMaterial;
  glass: THREE.MeshStandardMaterial;
  glassBand: THREE.MeshStandardMaterial;
  dark: THREE.MeshStandardMaterial;
  grey: THREE.MeshStandardMaterial;
  /** GS4b: three hue-jittered crate tints on the shared wood-grain map. */
  woods: readonly THREE.MeshStandardMaterial[];
  white: THREE.MeshStandardMaterial;
  checker: THREE.MeshStandardMaterial;
  /** M58 additions: chrome trim, the striped barrier board, the textured cone. */
  chrome: THREE.MeshStandardMaterial;
  stripes: THREE.MeshStandardMaterial;
  coneSkin: THREE.MeshStandardMaterial;
  /** Warm laterite sand skirt under Dakar's boulders. */
  sandSkirt: THREE.MeshStandardMaterial;
  /** Self-lit accents (Basic) — the bloom pass makes these glow at night. */
  rooflight: THREE.MeshBasicMaterial;
  shadow: THREE.MeshBasicMaterial;
  shadowStrong: THREE.MeshBasicMaterial;
  lamp: THREE.MeshBasicMaterial;
  headlamp: THREE.MeshBasicMaterial;
  taillamp: THREE.MeshBasicMaterial;
  /** Small base/top palette pairs so repeated boulders vary in tone (tints on
   *  a shared granite texture since M58); rocksWarm = the laterite set the
   *  desert cities (Dakar) tint toward. */
  rocks: readonly (readonly [THREE.MeshStandardMaterial, THREE.MeshStandardMaterial])[];
  rocksWarm: readonly (readonly [THREE.MeshStandardMaterial, THREE.MeshStandardMaterial])[];
  taxiWheel: THREE.CylinderGeometry;
  taxiHub: THREE.CylinderGeometry;
  cone: THREE.ConeGeometry;
  coneBase: THREE.CylinderGeometry;
  lampBulb: THREE.SphereGeometry;
  headlampBulb: THREE.SphereGeometry;
  checkerBand: THREE.PlaneGeometry;
  glassBandGeo: THREE.PlaneGeometry;
  sandSkirtGeo: THREE.SphereGeometry;
}

let obstacleKit: ObstacleKit | null = null;

function makeObstacleKit(): ObstacleKit {
  const std = (color: number, rough = 0.7): THREE.MeshStandardMaterial =>
    new THREE.MeshStandardMaterial({ color, roughness: rough, flatShading: true });
  // Crates: ONE shared grain map, three tint materials picked by index hash —
  // a dumped market stack varies in tone without touching the layout rng.
  const woodMap = crateTexture();
  const woodTint = (color: number): THREE.MeshStandardMaterial =>
    new THREE.MeshStandardMaterial({ color, roughness: 0.85, flatShading: true, map: woodMap });
  // One shared granite texture; the material colour TINTS it per palette
  // (base colours sit near-white so the multiply lands on the old grey tones).
  const rockMap = rockTexture();
  const rock = (color: number): THREE.MeshStandardMaterial =>
    new THREE.MeshStandardMaterial({ color, roughness: 0.95, flatShading: true, map: rockMap });
  // The cab: clearcoat breakup via a blotchy roughness map — a street car's
  // paint is waxed in patches, never one uniform sheen.
  const cab = new THREE.MeshStandardMaterial({
    color: CAB_YELLOW,
    roughness: 0.9,
    roughnessMap: weatherRoughTexture(),
    metalness: 0.12,
    flatShading: true,
  });
  return {
    cab,
    glass: std(0x1a2028, 0.2),
    glassBand: new THREE.MeshStandardMaterial({ map: glassBandTexture(), roughness: 0.25 }),
    dark: std(0x181a1f, 0.85),
    grey: std(0x888888, 0.7),
    woods: [woodTint(0xffffff), woodTint(0xf2ddbe), woodTint(0xd9c9c0)],
    white: std(0xe8e8e8, 0.6),
    checker: new THREE.MeshStandardMaterial({ map: checkerTexture(), roughness: 0.5 }),
    chrome: new THREE.MeshStandardMaterial({ color: 0xd9dde4, roughness: 0.22, metalness: 0.9 }),
    stripes: new THREE.MeshStandardMaterial({ map: stripeTexture(), roughness: 0.55 }),
    coneSkin: new THREE.MeshStandardMaterial({ map: coneTexture(), roughness: 0.6 }),
    sandSkirt: new THREE.MeshStandardMaterial({
      color: 0xd9a878,
      map: groundTexture('sand'),
      roughness: 0.97,
    }),
    rooflight: new THREE.MeshBasicMaterial({ color: 0xfff1bd }),
    shadow: new THREE.MeshBasicMaterial({
      map: contactShadowTexture(),
      transparent: true,
      depthWrite: false,
    }),
    shadowStrong: new THREE.MeshBasicMaterial({
      map: contactShadowTexture(0.62),
      transparent: true,
      depthWrite: false,
    }),
    lamp: new THREE.MeshBasicMaterial({ color: 0xffa22e }), // warm amber works-lamp
    headlamp: new THREE.MeshBasicMaterial({ color: 0xffe9b0 }),
    taillamp: new THREE.MeshBasicMaterial({ color: 0xff3540 }),
    rocks: [
      [rock(0xd8dce2), rock(0xf0f3f8)],
      [rock(0xe4e0d6), rock(0xf6f1e6)], // a warmer, sandier pair
      [rock(0xc6cad2), rock(0xe4e8ee)],
    ],
    rocksWarm: [
      [rock(0xe0b48e), rock(0xf2d4ac)], // laterite / dust-baked
      [rock(0xd8a878), rock(0xecc9a0)],
      [rock(0xe6c39c), rock(0xf6e2c2)],
    ],
    // GS4b: smaller wheels — the old 0.34 tyres read cartoon-oversized on the
    // sedan silhouette.
    taxiWheel: new THREE.CylinderGeometry(0.27, 0.27, 0.22, 10),
    taxiHub: new THREE.CylinderGeometry(0.125, 0.125, 0.24, 8),
    cone: new THREE.ConeGeometry(0.55, 1.2, 12),
    coneBase: new THREE.CylinderGeometry(0.5, 0.64, 0.14, 8),
    lampBulb: new THREE.SphereGeometry(0.15, 6, 4),
    headlampBulb: new THREE.SphereGeometry(0.07, 6, 4),
    checkerBand: new THREE.PlaneGeometry(3.1, 0.26),
    glassBandGeo: new THREE.PlaneGeometry(1.62, 0.4),
    sandSkirtGeo: new THREE.SphereGeometry(1, 9, 4, 0, Math.PI * 2, 0, Math.PI / 2.1),
  };
}

export function obstacleMesh(f: PlacedFeature, index: number, cityId: string): THREE.Group {
  // Deterministic little rotation so repeated props don't look cloned.
  const root = featureRoot(f, 0);
  root.rotation.y += ((index * 37) % 13) / 10 - 0.65;
  const kit = (obstacleKit ??= makeObstacleKit());
  // M99: contact shadow under every prop — grounds it on the road. GS4b: the
  // disc now clears the road-paint ribbon too (on Patagonia's opaque gravel
  // restyle it was depth-culled exactly like the ice slick), and crates get
  // the heavier variant (a stacked pile throws real shade).
  const shadowDisc = new THREE.Mesh(
    new THREE.PlaneGeometry(f.radius * (f.style === 'crate' ? 2.9 : 2.4), f.radius * 2.4),
    f.style === 'crate' ? kit.shadowStrong : kit.shadow,
  );
  shadowDisc.rotation.x = -Math.PI / 2;
  shadowDisc.position.y = MARKINGS_CLEAR - 0.008;
  shadowDisc.renderOrder = DECAL_ORDER - 1; // over the ribbon, under slick decals
  shadowDisc.userData.keepOnSwap = true; // M158: stays when a model replaces the stand-in
  root.add(shadowDisc);
  const box = (
    w: number,
    h: number,
    d: number,
    mat: THREE.Material,
    y: number,
    x = 0,
    z = 0,
  ): THREE.Mesh => {
    const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
    m.position.set(x, y, z);
    return m;
  };
  switch (f.style) {
    case 'taxi': {
      // M58 rebuild: a REAL sedan silhouette (extruded side profile, the same
      // trick as the racers' cars) — hood → windshield → roof → trunk — with
      // a separate glasshouse, chrome bumpers, lit head/tail lamps, hubcapped
      // wheels, the lit TAXI roof box and the checker band down each side.
      const bodyMesh = new THREE.Mesh(extrudeSide(taxiBodyProfile(), 1.66), kit.cab);
      const cabinMesh = new THREE.Mesh(extrudeSide(taxiCabinProfile(), 1.5), kit.glass);
      root.add(bodyMesh, cabinMesh);
      root.add(
        box(1.68, 0.16, 0.2, kit.chrome, 0.4, 0, 1.72), // front bumper
        box(1.68, 0.16, 0.2, kit.chrome, 0.4, 0, -1.72), // rear bumper
        box(0.9, 0.14, 0.06, kit.dark, 0.55, 0, 1.74), // grille
        box(1.2, 0.08, 0.05, kit.taillamp, 0.68, 0, -1.72), // tail-light strip
      );
      for (const side of [-1, 1] as const) {
        const lampDot = new THREE.Mesh(kit.headlampBulb, kit.headlamp);
        lampDot.scale.set(1.6, 1.2, 1);
        lampDot.position.set(side * 0.55, 0.6, 1.73);
        root.add(lampDot);
      }
      for (const [wx, wz] of [
        [-0.78, 1.15],
        [0.78, 1.15],
        [-0.78, -1.15],
        [0.78, -1.15],
      ] as const) {
        const w = new THREE.Mesh(kit.taxiWheel, kit.dark);
        w.rotation.z = Math.PI / 2; // cylinder axis across the car
        w.position.set(wx, 0.27, wz);
        const hub = new THREE.Mesh(kit.taxiHub, kit.chrome);
        w.add(hub); // hubcap gleams inside the tyre
        root.add(w);
      }
      root.add(box(0.46, 0.16, 0.26, kit.rooflight, 1.45, 0, -0.3)); // lit TAXI box
      for (const side of [-1, 1] as const) {
        const bandMesh = new THREE.Mesh(kit.checkerBand, kit.checker);
        bandMesh.rotation.y = (side * Math.PI) / 2;
        bandMesh.position.set(side * 0.845, 0.6, 0); // flush to the body side
        root.add(bandMesh);
        // GS4b: painted side glazing (door panes + pillars) — the BODY
        // extrusion (±0.83) encloses the glasshouse's sides, so the flank
        // read yellow to the roofline; the band sits a hair PROUD of the
        // body face, tucked under the roof edge.
        const glazing = new THREE.Mesh(kit.glassBandGeo, kit.glassBand);
        glazing.rotation.y = (side * Math.PI) / 2;
        glazing.position.set(side * 0.845, 1.02, -0.33);
        root.add(glazing);
      }
      // M158: the hand-built taxi sedan replaces this stand-in once loaded.
      attachPropModel(root, NYC_TAXI);
      break;
    }
    case 'rock': {
      // M58: lumpy DISPLACED boulders (position-hashed vertex jitter) with a
      // shared granite texture — real rock silhouettes, tinted per palette,
      // plus a small shard at the foot like genuine rockfall debris. GS4b:
      // desert cities pull from the warm LATERITE tint set and grow a heaped
      // sand skirt at the base (a grey glacier boulder read wrong in Dakar).
      const warm = cityId === 'dakar';
      const set = warm ? kit.rocksWarm : kit.rocks;
      const [baseMat, topMat] = set[index % set.length];
      const base = new THREE.Mesh(boulderGeometry(f.radius, index * 3 + 1, 3), baseMat);
      base.position.y = f.radius * 0.55;
      const top = new THREE.Mesh(boulderGeometry(f.radius * 0.5, index * 5 + 2, 2), topMat);
      top.position.set(f.radius * 0.28, f.radius * 1.0, -f.radius * 0.14);
      const shard = new THREE.Mesh(boulderGeometry(f.radius * 0.3, index * 7 + 3, 2), baseMat);
      shard.position.set(-f.radius * 0.75, f.radius * 0.16, f.radius * 0.45);
      root.add(base, top, shard);
      if (warm) {
        const skirt = new THREE.Mesh(kit.sandSkirtGeo, kit.sandSkirt);
        skirt.scale.set(f.radius * 1.55, 0.42, f.radius * 1.45);
        skirt.position.y = 0.03;
        root.add(skirt);
      }
      break;
    }
    case 'crate': {
      // Slight per-crate twists + hue-jittered tints — a dumped market
      // stack, not a copy-paste (tint choice from the index hash, never rng).
      const tint = (n: number): THREE.MeshStandardMaterial =>
        kit.woods[Math.floor(art(index * 11 + n * 3) * kit.woods.length) % kit.woods.length];
      const c1 = box(1.4, 1.4, 1.4, tint(0), 0.7, -0.5, 0);
      c1.rotation.y = 0.14;
      const c2 = box(1.2, 1.2, 1.2, tint(1), 0.6, 0.9, 0.3);
      c2.rotation.y = -0.22;
      const c3 = box(1.1, 1.1, 1.1, tint(2), 1.95, -0.3, 0.1);
      c3.rotation.y = 0.38;
      root.add(c1, c2, c3);
      break;
    }
    case 'barrier': {
      // M58 rebuild: the universal roadworks barrier — a red/white DIAGONAL-
      // striped board on two A-frame legs with feet, topped by the amber lamp.
      root.add(
        box(2.8, 0.55, 0.09, kit.stripes, 0.82), // the striped board
        box(2.9, 0.08, 0.14, kit.dark, 1.14), // top rail
      );
      for (const side of [-1, 1] as const) {
        const legA = box(0.09, 1.15, 0.09, kit.white, 0.55, side * 1.2, 0.22);
        legA.rotation.x = -0.34;
        const legB = box(0.09, 1.15, 0.09, kit.white, 0.55, side * 1.2, -0.22);
        legB.rotation.x = 0.34;
        root.add(legA, legB, box(0.46, 0.07, 0.7, kit.dark, 0.035, side * 1.2, 0));
      }
      root.add(box(0.12, 0.14, 0.12, kit.dark, 1.24)); // lamp mount
      const bulb = new THREE.Mesh(kit.lampBulb, kit.lamp);
      bulb.position.y = 1.38;
      root.add(bulb);
      break;
    }
    case 'cone': {
      // M58: textured skin (orange fade + baked reflective band + grime) on a
      // moulded rubber base puck instead of the flat plate.
      const cone = new THREE.Mesh(kit.cone, kit.coneSkin);
      cone.position.y = 0.72;
      const basePuck = new THREE.Mesh(kit.coneBase, kit.dark);
      basePuck.position.y = 0.07;
      root.add(basePuck, cone);
      break;
    }
    case 'scooter': {
      // GS4b: two parked Vespas from the SHARED propKit build (the same
      // silhouette the Napoli mover rides) — leg shield, saddle, chrome bars,
      // round headlamp, real wheels — riderless, on their kickstands, leaned
      // slightly onto the stand like a kerbside cluster.
      for (const [n, [sx, sz, ry, body]] of (
        [
          [-0.4, -0.1, 0.18, 0x9fd8c4], // pastel mint — the classic
          [0.7, 0.35, -0.12, 0xe8e0d0], // cream
        ] as const
      ).entries()) {
        const vespa = buildVespaProp({ body, rider: false, stand: true });
        vespa.group.position.set(sx, 0, sz);
        vespa.group.rotation.y = ry;
        vespa.group.rotation.z = 0.06 + art(index * 7 + n) * 0.04; // resting on the stand
        attachPropModel(vespa.group, vespaStyle(body));
        root.add(vespa.group);
      }
      break;
    }
    default: {
      root.add(box(1.5, 1, 1.5, kit.grey, 0.5));
    }
  }
  return root;
}

// ---- Ramp identity pass (GS5) ------------------------------------------------
// Every launcher reads as a real city object; the PHYSICS wedge is untouched
// (the drivable top surface stays the exact incline in every build — the M99
// lesson). Dispatch on RampSpec.build; the M117 shortcut launchers (NYC works
// zone / Tokyo expressway / the Lagos jetty's sky-path) are NOT these — the
// jetty is the only shared piece and it stays byte-identical.

/** Grayscale brushed dark-metal panels (Tokyo's ramp body — tinted by
 *  ramp.color): grind streaks + panel seams + wear scuffs. */
function rampMetalTexture(): THREE.CanvasTexture {
  const [c, x] = makeCanvas(128, 128);
  x.fillStyle = '#8e8e92';
  x.fillRect(0, 0, 128, 128);
  for (let i = 0; i < 90; i++) {
    x.fillStyle = art(i * 3 + 300) > 0.5 ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.12)';
    x.fillRect(art(i * 5 + 301) * 128, art(i * 7 + 302) * 128, 1.5, 14 + art(i * 11 + 303) * 40);
  }
  x.fillStyle = 'rgba(0,0,0,0.4)';
  for (const sy of [32, 64, 96]) x.fillRect(0, sy, 128, 1.6); // panel seams
  for (let i = 0; i < 20; i++) {
    x.fillStyle = 'rgba(255,255,255,0.1)';
    x.fillRect(art(i * 13 + 310) * 128, art(i * 17 + 311) * 128, 4 + art(i * 19) * 8, 1);
  }
  return canvasTex(c);
}

/** Grayscale cut-block courses (tinted per city: dark = Mumbai basalt, warm
 *  cream = Napoli tufa). `pitting` adds the volcanic pock speckle. */
function blockRampTexture(pitting: boolean): THREE.CanvasTexture {
  const [c, x] = makeCanvas(128, 128);
  x.fillStyle = '#9a9a96';
  x.fillRect(0, 0, 128, 128);
  const rows = 4;
  for (let r = 0; r < rows; r++) {
    const y0 = r * 32;
    const off = (r % 2) * 22;
    for (let bx = -22; bx < 128; bx += 44) {
      const jx = bx + off;
      const tone = 138 + Math.floor(art(r * 31 + bx) * 40);
      x.fillStyle = `rgb(${tone},${tone - 2},${tone - 6})`;
      x.fillRect(jx + 2, y0 + 2, 40, 28);
      // Mortar joints (dark) + a top catch-light per block.
      x.fillStyle = 'rgba(30,28,24,0.65)';
      x.fillRect(jx, y0, 44, 2);
      x.fillRect(jx, y0, 2, 32);
      x.fillStyle = 'rgba(255,255,255,0.1)';
      x.fillRect(jx + 2, y0 + 2, 40, 2);
    }
  }
  if (pitting) {
    for (let i = 0; i < 240; i++) {
      x.fillStyle = `rgba(40,36,30,${0.15 + art(i * 3 + 400) * 0.25})`;
      const r = 0.6 + art(i * 7 + 401) * 1.6;
      x.beginPath();
      x.arc(art(i * 5 + 402) * 128, art(i * 11 + 403) * 128, r, 0, Math.PI * 2);
      x.fill();
    }
  }
  return canvasTex(c);
}

/** Sand shell for the Rio/Dakar wedges: grain speckle, and (dune) strong
 *  wind-ripple wave lines. Grayscale — tinted by ramp.color. */
function sandRampTexture(ripples: boolean): THREE.CanvasTexture {
  const [c, x] = makeCanvas(256, 256);
  x.fillStyle = '#b6b0a4';
  x.fillRect(0, 0, 256, 256);
  if (ripples) {
    x.strokeStyle = 'rgba(70,58,40,0.4)';
    x.lineWidth = 5;
    for (let row = 0; row < 9; row++) {
      const y0 = 16 + row * 27 + art(500 + row) * 8;
      x.beginPath();
      for (let px = -6; px <= 262; px += 10) {
        const py = y0 + Math.sin(px * 0.05 + row * 1.7) * 7;
        if (px < 0) x.moveTo(px, py);
        else x.lineTo(px, py);
      }
      x.stroke();
    }
  } else {
    // Softer moisture mottling for the beach wedge.
    for (let i = 0; i < 26; i++) {
      const g2 = x.createRadialGradient(0, 0, 0, 0, 0, 20 + art(i * 7 + 520) * 26);
      g2.addColorStop(0, 'rgba(90,76,54,0.16)');
      g2.addColorStop(1, 'rgba(0,0,0,0)');
      x.save();
      x.translate(art(i * 3 + 521) * 256, art(i * 5 + 522) * 256);
      x.fillStyle = g2;
      x.fillRect(-48, -48, 96, 96);
      x.restore();
    }
  }
  for (let i = 0; i < 1400; i++) {
    const t = art(i * 3 + 540);
    x.fillStyle = t < 0.5 ? 'rgba(60,48,30,0.3)' : 'rgba(255,250,238,0.3)';
    x.fillRect(art(i * 5 + 541) * 256, art(i * 7 + 542) * 256, 1, 1);
  }
  return canvasTex(c);
}

/** Snow shell (map + roughness): near-white with blue-grey wind shading and
 *  sparse SPARKLE specks; the rough map varies sheen so the heap reads packed
 *  in some patches and fresh in others. Tinted by ramp.color. */
function snowRampTextures(): { map: THREE.CanvasTexture; rough: THREE.CanvasTexture } {
  const [c, x] = makeCanvas(256, 256);
  const [rc, rx] = makeCanvas(128, 128);
  x.fillStyle = '#f4f8fc';
  x.fillRect(0, 0, 256, 256);
  rx.fillStyle = '#9e9e9e';
  rx.fillRect(0, 0, 128, 128);
  for (let i = 0; i < 30; i++) {
    const bx = art(i * 3 + 600) * 256;
    const by = art(i * 5 + 601) * 256;
    const br = 18 + art(i * 7 + 602) * 42;
    const g = x.createRadialGradient(bx, by, 0, bx, by, br);
    g.addColorStop(0, `rgba(168, 188, 208, ${0.12 + art(i * 11 + 603) * 0.14})`);
    g.addColorStop(1, 'rgba(0,0,0,0)');
    x.fillStyle = g;
    x.fillRect(bx - br, by - br, br * 2, br * 2);
    const rg = rx.createRadialGradient(bx / 2, by / 2, 0, bx / 2, by / 2, br / 2);
    rg.addColorStop(0, art(i * 13 + 604) > 0.5 ? 'rgba(60,60,60,0.5)' : 'rgba(210,210,210,0.5)');
    rg.addColorStop(1, 'rgba(0,0,0,0)');
    rx.fillStyle = rg;
    rx.fillRect(bx / 2 - br / 2, by / 2 - br / 2, br, br);
  }
  // Sparkle: rare hot single-pixel glints (the sun catching ice crystals).
  for (let i = 0; i < 110; i++) {
    x.fillStyle = 'rgba(255,255,255,0.95)';
    x.fillRect(art(i * 5 + 620) * 256, art(i * 7 + 621) * 256, 1.4, 1.4);
    rx.fillStyle = '#141414'; // glints are glossy
    rx.fillRect(art(i * 5 + 620) * 128, art(i * 7 + 621) * 128, 1, 1);
  }
  const rough = new THREE.CanvasTexture(rc);
  rough.colorSpace = THREE.NoColorSpace;
  return { map: canvasTex(c), rough };
}

let driftTex: THREE.CanvasTexture | null = null;
/** Feathered irregular drift blob (white — tint via material colour): the
 *  skirt decal sand/snow ramps feather onto the asphalt with. */
function driftTexture(): THREE.CanvasTexture {
  if (driftTex) return driftTex;
  const [c, x] = makeCanvas(128, 128);
  blobPath(x, 64, 64, 58, 91);
  x.fillStyle = 'rgba(255,255,255,0.78)';
  x.fill();
  featherEdge(x, 128, 0.32); // long soft falloff — a wind-fanned spill, not a spot
  driftTex = canvasTex(c);
  return driftTex;
}

/** The pitched-box wedge shared by every smooth build: top face = the exact
 *  physics incline ('YXZ': yaw first, −pitch raises the down-course end). */
function wedgeMesh(
  track: Track,
  ramp: RampSpec,
  start: number,
  thick: number,
  mat: THREE.Material | THREE.Material[],
): THREE.Mesh {
  const pitch = Math.atan(ramp.height / ramp.length);
  const mid = track.sampleAt(start + ramp.length / 2);
  const wedge = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), mat);
  wedge.setRotationFromEuler(new THREE.Euler(-pitch, mid.dir, 0, 'YXZ'));
  wedge.position.set(mid.x, mid.y + ramp.height / 2 - (thick / 2) * Math.cos(pitch), mid.z);
  wedge.scale.set(ramp.halfWidth * 2, thick, Math.hypot(ramp.length, ramp.height));
  return wedge;
}

/** Drift-skirt decals feathering a heaped ramp onto the asphalt around its
 *  foot and flanks (above the road-paint ribbon — the GS4b layering law). */
function addDriftSkirts(
  g: THREE.Group,
  track: Track,
  ramp: RampSpec,
  start: number,
  color: number,
  seed: number,
): void {
  const mat = new THREE.MeshStandardMaterial({
    map: driftTexture(),
    color,
    transparent: true,
    depthWrite: false,
    roughness: 1,
    metalness: 0,
  });
  for (let i = 0; i < 6; i++) {
    const side = i % 2 === 0 ? 1 : -1;
    const foot = i < 2; // two at the approach foot, four along the flanks
    const arc = foot ? start - 1.2 - art(seed + i * 3) * 2.2 : start + art(seed + i * 5) * ramp.length * 0.85;
    const s = track.sampleAt(arc);
    const lat = foot ? (art(seed + i * 7) - 0.5) * ramp.halfWidth * 1.4 : side * (ramp.halfWidth + 1.1 + art(seed + i * 7) * 1.3);
    const decal = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mat);
    decal.rotation.x = -Math.PI / 2;
    decal.rotation.z = art(seed + i * 11) * Math.PI * 2;
    const w = 3.2 + art(seed + i * 13) * 3.4;
    decal.scale.set(w, w * 0.7, 1);
    decal.position.set(
      s.x + s.rightX * lat,
      s.y + MARKINGS_CLEAR + 0.002 + i * 0.004,
      s.z + s.rightZ * lat,
    );
    decal.renderOrder = DECAL_ORDER;
    g.add(decal);
  }
}

/** Heaped berm lumps along a wedge's flanks (+ rounding the lip corners) —
 *  the spill that hides the clean box edge on the sand/snow builds. */
function addBermLumps(
  g: THREE.Group,
  track: Track,
  ramp: RampSpec,
  start: number,
  mat: THREE.Material,
  seed: number,
): void {
  for (let i = 0; i < 9; i++) {
    const side = i % 2 === 0 ? 1 : -1;
    const corner = i >= 7; // the last two round the lip's box corners
    const t = corner ? 0.97 : 0.06 + art(seed + i * 5) * 0.88;
    const s = track.sampleAt(start + t * ramp.length);
    const r = corner ? 1.5 : 0.9 + art(seed + i * 7) * 1.3;
    const lat = side * (ramp.halfWidth + 0.15 + (corner ? 0.35 : art(seed + i * 11) * 0.9));
    const lump = new THREE.Mesh(rockGeometry(r, seed + i * 13), mat);
    lump.scale.y = 0.42;
    // Flank lumps sit on the road hugging the wedge; corner lumps ride at
    // deck height so the sharp top corner reads heaped, not moulded.
    const y = corner ? s.y + t * ramp.height - r * 0.28 : s.y + r * 0.2;
    lump.position.set(s.x + s.rightX * lat, y, s.z + s.rightZ * lat);
    lump.rotation.y = art(seed + i * 17) * Math.PI * 2;
    g.add(lump);
  }
}

/** GS5 Rio/Dakar: the sand wedge as a real drift — grain-textured shell,
 *  wind ripples (dune), heaped berm skirts and feathered drift decals. */
function buildSandRamp(
  g: THREE.Group,
  track: Track,
  ramp: RampSpec,
  start: number,
  ripples: boolean,
): void {
  const tint = ramp.color ?? 0xd8b070;
  const mat = new THREE.MeshStandardMaterial({
    map: sandRampTexture(ripples),
    color: tint,
    roughness: 0.96,
    metalness: 0,
  });
  g.add(wedgeMesh(track, ramp, start, 1.2, mat));
  const berm = new THREE.MeshStandardMaterial({
    map: groundTexture('sand'),
    color: new THREE.Color(tint).multiplyScalar(0.88),
    roughness: 0.97,
    flatShading: true,
  });
  addBermLumps(g, track, ramp, start, berm, ripples ? 61 : 37);
  addDriftSkirts(g, track, ramp, start, tint, ripples ? 62 : 38);
}

/** GS5 Patagonia: the snow bank as a heaped drift — noise-displaced side
 *  shells, sparkle + roughness variation, drift skirts feathering out. */
function buildSnowRamp(g: THREE.Group, track: Track, ramp: RampSpec, start: number): void {
  const skins = snowRampTextures();
  const tint = ramp.color ?? 0xdfe8f2;
  const mat = new THREE.MeshStandardMaterial({
    map: skins.map,
    roughnessMap: skins.rough,
    color: tint,
    roughness: 1,
    metalness: 0,
    envMapIntensity: 1.25,
  });
  g.add(wedgeMesh(track, ramp, start, 1.2, mat));
  const heap = new THREE.MeshStandardMaterial({
    map: skins.map,
    color: 0xf2f7fc,
    roughness: 0.72,
    flatShading: true,
  });
  addBermLumps(g, track, ramp, start, heap, 83);
  addDriftSkirts(g, track, ramp, start, 0xf4f8fc, 84);
}

/** The bus ramp's LIP-END face: a WIDE open-top Routemaster tail — cream
 *  between-decks band, the open rear PLATFORM with the staircase rising
 *  inside, route roundel and tail lamps. Painted bespoke because the propKit
 *  rear canvas is portrait (a real bus tail) and stretching it across the
 *  10 u wedge face read as red letterbox slits (measured). */
function busRampRearTexture(): { color: HTMLCanvasElement; emissive: HTMLCanvasElement } {
  const [c, x] = makeCanvas(512, 160);
  const [ce, e] = makeCanvas(512, 160);
  e.fillStyle = '#000';
  e.fillRect(0, 0, 512, 160);
  x.fillStyle = '#b81a2b';
  x.fillRect(0, 0, 512, 160);
  // Panel shading + the cream between-decks band.
  x.fillStyle = 'rgba(0,0,0,0.12)';
  x.fillRect(0, 132, 512, 28); // skirt
  x.fillStyle = '#e8e2d0';
  x.fillRect(0, 62, 512, 12);
  // Upper-deck rear window row.
  x.fillStyle = '#15171c';
  x.fillRect(24, 14, 464, 38);
  for (let i = 0; i < 6; i++) {
    const px = 30 + i * 77;
    const lit = art(i * 7 + 700) < 0.7;
    x.fillStyle = lit ? '#f5c86e' : '#232b36';
    x.fillRect(px, 18, 66, 30);
    if (lit) {
      e.fillStyle = '#ffd98c';
      e.fillRect(px, 18, 66, 30);
    }
  }
  // The OPEN REAR PLATFORM (right half, kerb side): dark opening, grab pole,
  // and the staircase silhouette climbing left.
  x.fillStyle = '#171310';
  x.fillRect(300, 80, 176, 56);
  x.fillStyle = '#242019';
  for (let s2 = 0; s2 < 5; s2++) {
    x.fillRect(310 + s2 * 30, 128 - s2 * 11, 34, 8); // stair treads
  }
  x.fillStyle = '#cfc4a8';
  x.fillRect(462, 82, 5, 52); // platform grab pole
  const warm = e.createLinearGradient(300, 0, 476, 0);
  warm.addColorStop(0, 'rgba(120,84,40,0.55)');
  warm.addColorStop(1, 'rgba(60,40,20,0.2)');
  e.fillStyle = warm;
  e.fillRect(300, 80, 176, 56); // the platform's warm interior spill
  // Lower-deck rear window (left of the platform).
  x.fillStyle = '#15171c';
  x.fillRect(36, 84, 240, 40);
  x.fillStyle = '#232b36';
  x.fillRect(42, 88, 228, 32);
  // Roundel + route board on the cream band.
  x.fillStyle = '#7a1220';
  x.font = 'bold 12px system-ui, sans-serif';
  x.textAlign = 'center';
  x.textBaseline = 'middle';
  x.fillText('88 · TOWER HILL', 256, 68);
  // Tail lamps.
  for (const lx of [30, 482]) {
    x.fillStyle = '#0e0f12';
    x.beginPath();
    x.arc(lx, 144, 9, 0, Math.PI * 2);
    x.fill();
    x.fillStyle = '#ff3540';
    x.beginPath();
    x.arc(lx, 144, 6.4, 0, Math.PI * 2);
    x.fill();
    e.fillStyle = '#ff3540';
    e.beginPath();
    e.arc(lx, 144, 6.4, 0, Math.PI * 2);
    e.fill();
  }
  return { color: c, emissive: ce };
}

/** GS5 London: the wedge dressed as a TILTED PARKED DOUBLE-DECKER — nosed
 *  down into the road, tail up, and you launch off its roof. The propKit bus
 *  canvases wear the flanks (two lit window decks + route board), the roof
 *  IS the drivable incline, and the lip-end face is the open-top rear
 *  platform with its staircase. Physics untouched: the box top face is the
 *  exact same incline plane. THICK 3.4 keeps the tail's underside buried
 *  (2.4 left a visible float gap under the lip face — measured). */
function buildBusRamp(g: THREE.Group, track: Track, ramp: RampSpec, start: number): void {
  const set = doubleDeckerCanvases({ route: '88', dest: 'TOWER HILL', seed: 21 });
  const rear = busRampRearTexture();
  // Painted-box face order is [+x,−x,roof,floor,+z,−z]; local +Z faces
  // down-course = the LIP end, which wears the bespoke wide tail canvas. The
  // roof is a darker, busier variant than the mover's (it IS the road here —
  // the pale cream read as a bare concrete slab in overcast light).
  const mats = paintedBoxMaterials({
    side: set.side,
    front: rear,
    rear: set.front,
    roof: vehicleRoofCanvasLocal({ paint: '#c9c2ae', vents: 2, seams: 4, grime: 0.45 }),
    roughness: 0.62,
    emissiveIntensity: 0.5,
  });
  const THICK = 3.4;
  g.add(wedgeMesh(track, ramp, start, THICK, mats));
  // Wheels tucked against the painted flanks at road level — the parked read
  // (the tipped body's true underside is buried, so the wheels are the one
  // grounding cue). Parked: no spin.
  for (const t of [0.55, 0.85]) {
    const s = track.sampleAt(start + t * ramp.length);
    for (const side of [-1, 1] as const) {
      const wheel = makeRoadWheel(0.52, 0.36, { hub: 0x2a2c31 });
      const lat = side * (ramp.halfWidth - 0.1);
      wheel.pivot.position.set(s.x + s.rightX * lat, s.y + 0.5, s.z + s.rightZ * lat);
      wheel.pivot.rotation.y = s.dir;
      g.add(wheel.pivot);
    }
  }
}

/** Local mirror of propKit's roof painter shape (kept here so the ramp can
 *  tune its roof without widening the shared kit's surface). */
function vehicleRoofCanvasLocal(opts: {
  paint: string;
  vents: number;
  seams: number;
  grime: number;
}): HTMLCanvasElement {
  const [c, x] = makeCanvas(128, 256);
  x.fillStyle = opts.paint;
  x.fillRect(0, 0, 128, 256);
  const edge = x.createLinearGradient(0, 0, 128, 0);
  edge.addColorStop(0, 'rgba(0,0,0,0.3)');
  edge.addColorStop(0.16, 'rgba(0,0,0,0)');
  edge.addColorStop(0.84, 'rgba(0,0,0,0)');
  edge.addColorStop(1, 'rgba(0,0,0,0.3)');
  x.fillStyle = edge;
  x.fillRect(0, 0, 128, 256);
  for (let i = 0; i < opts.vents; i++) {
    const vx = (128 * (i + 1)) / (opts.vents + 1);
    x.fillStyle = 'rgba(20,22,26,0.6)';
    x.fillRect(vx - 2, 24, 4, 208);
  }
  for (let i = 1; i <= opts.seams; i++) {
    x.fillStyle = 'rgba(0,0,0,0.24)';
    x.fillRect(0, (256 * i) / (opts.seams + 1), 128, 2);
  }
  for (let i = 0; i < 40; i++) {
    x.fillStyle = `rgba(30,26,18,${0.1 + art(i * 3 + 720) * 0.2 * opts.grime})`;
    x.fillRect(art(i * 5 + 721) * 128, art(i * 7 + 722) * 256, 2 + art(i * 11) * 4, 1.6);
  }
  return c;
}

/** The body material for the STEPPED builds (per RampSpec.build). */
function steppedRampMaterial(ramp: RampSpec): THREE.MeshStandardMaterial {
  switch (ramp.build) {
    case 'neon':
      // Tokyo (GS5): DARK METAL body — the old full-face cyan emissive made
      // the whole ramp one glowing slab and washed out its own checker strip;
      // the glow now lives ONLY in the edge rails + lip bar.
      return new THREE.MeshStandardMaterial({
        color: ramp.color ?? 0x16181f,
        map: rampMetalTexture(),
        roughness: 0.42,
        metalness: 0.6,
        flatShading: true,
      });
    case 'basalt':
      // Mumbai: dark sea-wall basalt blocks (daylight cues come from the
      // nosings/studs/checker, never from emissive — sun 2.3 ate the glow).
      return new THREE.MeshStandardMaterial({
        color: ramp.color ?? 0x4a4640,
        map: blockRampTexture(true),
        roughness: 0.9,
        metalness: 0,
        flatShading: true,
      });
    case 'tufa':
      // Napoli: warm tufa block courses, matte volcanic stone.
      return new THREE.MeshStandardMaterial({
        color: ramp.color ?? 0xd8c07a,
        map: blockRampTexture(true),
        roughness: 0.85,
        metalness: 0,
        flatShading: true,
      });
    default:
      // NYC's red stairs (GS5): the TKTS glass-stair read — faint inner red
      // glow + GLOSSY riser faces catching the Times Square light (the old
      // 0.5-intensity emissive over rough plastic read as painted plywood).
      return new THREE.MeshStandardMaterial({
        color: ramp.color ?? 0xd2202c,
        roughness: 0.18,
        metalness: 0,
        envMapIntensity: 1.7,
        emissive: ramp.emissive ?? 0x6a0a12,
        emissiveIntensity: 0.32,
        flatShading: true,
      });
  }
}

/**
 * THE TKTS STEPS (nyc-park round, owner: "the red stairs read slightly inert
 * beside the billboards — fix this and make these stairs higher too").
 *
 * MEASURED first, from the driver's seat at arc 1918: against Times Square's
 * wall of full-brightness MeshBasic boards the stairs render as one squat matte
 * maroon wedge with faint horizontal lines. Two separate problems, and the
 * second one is a trap:
 *
 *  1. HEIGHT. `ramp.height` is PHYSICS (physics/ramp.ts: launch vy =
 *     forwardSpeed × height/length), so that round took the height from
 *     STRUCTURE instead — two flanking stair WINGS outboard of the jumpable
 *     halfWidth, climbing to 2.8 × the ramp and carrying on three courses PAST
 *     the lip into a landing, topped by a glass balustrade and a pale handrail.
 *     Real Duffy Square is exactly this: a grandstand that rises to a rail,
 *     with the sloped run in the middle.
 *
 *     nyc-lake ROUND 2, owner: "make the center part of the TKTS stairs the
 *     same height as the adjusted height you made the sides, and increase the
 *     jump physics respectively" — so the run itself now climbs to the wings'
 *     old crest (`ramp.height` 3 → 8.4, `length` 9 → 17 to keep the face at a
 *     walkable 26°, cities.ts) and this is a SIM change, carried by SIM_VERSION
 *     25. The wings stay a step proud of it (see wingRise).
 *  2. INERTNESS. The M118 lesson applies verbatim — a flat emissive tint with
 *     no map washes a slab into a glowing blob (it is why Tokyo's neon ramp
 *     dropped its full-face emissive). So the light is STRUCTURED: one thin
 *     unlit-material bar under each tread's nosing, the way the real steps are
 *     back-lit ruby glass, with a per-step brightness ripple off the index hash
 *     so the stack reads as many lit edges rather than one red panel. Those
 *     bars are the only thing here that ignores tone mapping, which is what
 *     lets them hold their own against the billboards.
 *
 * Everything below is DISPLAY ONLY, outboard of `ramp.halfWidth` or flush under
 * a tread — the ramp surface the physics rides is byte-identical.
 */
const TKTS = {
  /** Wing width, outboard of the ramp's own halfWidth.
   *
   *  HARD CEILING, measured: the M117d Times Square traffic sits at |lateral|
   *  10.5 with radius 2, and tests/physics/shortcuts.test.ts requires
   *  |lateral| − radius − halfWidth ≥ 1.5 of clear road beside the ramp — i.e.
   *  the band 5.0-8.5 is the "go around instead of jumping" lane and it has
   *  exactly 3.5 u in it. Scenery is not a collider, so a wide wing does not
   *  BLOCK that lane, it just lets a weaving car drive visibly through solid
   *  masonry: at 3.4 the wing reached 8.4 and closed the gap to 0.1 u. 1.8
   *  leaves 1.7 — the test's own margin — so the flank reads as the real TKTS
   *  balustrade wall rather than a grandstand parked in the racing line. */
  wingWidth: 1.8,
  /** Wing top height as a multiple of ramp.height.
   *
   *  nyc-lake, owner: "make the center part of the TKTS stairs the same height
   *  as the adjusted height you made the sides". The previous round's 2.8 was
   *  the whole trick for gaining height WITHOUT touching physics; now that the
   *  centre run has been raised to that crest (cities.ts `ramp.height` 3 → 8.4)
   *  the multiplier has to come back to ~1 or the wings would shoot to 23 u and
   *  the stairs would be a canyon. wingH() tops out at
   *  `height × (0.35 + ((courses−0.5)/courses) × (wingRise − 0.35))`, so at
   *  courses = 14 (steps 11 + 3 landing) a wingRise of 1.08 puts the crest
   *  ≈ 1.05 × height — the balustrade wall standing a step proud of the run it
   *  flanks, which is what Duffy Square actually looks like. */
  wingRise: 1.08,
  /** Wing height at the FOOT, as a multiple of ramp.height.
   *
   *  It was a bare 0.35 literal, which was invisible while ramp.height was 3
   *  (a 1.05 u kerb) and became a 2.9 u WALL the moment the height went to 8.4
   *  — a chest-high red block sitting either side of the ramp foot, plainly
   *  wrong in a side shot. 0.12 puts the first wing course at ~1.3 u, i.e. a
   *  step or two above the road, so the flanks grow out of the pavement the way
   *  a real grandstand does. */
  wingBase: 0.12,
  /** Wing courses continuing PAST the lip (the upper landing). They sit
   *  outboard of the launch lane, so a car in flight never meets them. */
  landingCourses: 3,
  /** Glass balustrade above the wing crest. */
  railHeight: 1.7,
  /** Hot ruby of the back-lit tread edges. */
  glass: 0xff2a3c,
} as const;

function buildTktsStair(
  g: THREE.Group,
  track: Track,
  ramp: RampSpec,
  start: number,
  steps: number,
  bodyMat: THREE.Material,
): void {
  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const e = new THREE.Euler();
  const p = new THREE.Vector3();
  const sc = new THREE.Vector3();
  const segLen = ramp.length / steps + 0.4;
  const courses = steps + TKTS.landingCourses;
  // The wings ride the same tread rhythm as the centre run, then keep climbing
  // past the lip. `t` walks 0..1 over the whole flight, so the crest is level.
  const wingH = (i: number): number =>
    ramp.height * (TKTS.wingBase + ((i + 0.5) / courses) * (TKTS.wingRise - TKTS.wingBase));
  const wingAt = (i: number): number => start + ((i + 0.5) / steps) * ramp.length;
  const wingLat = ramp.halfWidth + TKTS.wingWidth / 2;

  // --- the two stair wings (same body material: one more instanced mesh) ---
  const wings = new THREE.InstancedMesh(new THREE.BoxGeometry(1, 1, 1), bodyMat, courses * 2);
  // --- back-lit tread edges: centre run + wings, one thin bar per course ---
  const barCount = steps + courses * 2;
  const barMat = new THREE.MeshBasicMaterial({ color: TKTS.glass, toneMapped: false });
  const bars = new THREE.InstancedMesh(new THREE.BoxGeometry(1, 1, 1), barMat, barCount);
  const barColor = new THREE.Color();
  let bar = 0;
  const litEdge = (s: TrackSample, top: number, lat: number, width: number, key: number): void => {
    e.set(0, s.dir, 0);
    q.setFromEuler(e);
    p.set(
      s.x + s.rightX * lat,
      s.y + top - 0.12,
      s.z + s.rightZ * lat,
    );
    sc.set(width, 0.2, 0.16);
    bars.setMatrixAt(bar, m.compose(p, q, sc));
    // A per-step brightness ripple: the real stair's panels are never a single
    // even sheet, and one flat value is exactly the "glowing blob" trap.
    bars.setColorAt(bar, barColor.setHex(TKTS.glass).multiplyScalar(0.62 + art(key * 3 + 91) * 0.5));
    bar++;
  };

  for (let i = 0; i < courses; i++) {
    const along = Math.min(i, steps - 1);
    const s = track.sampleAt(
      i < steps ? wingAt(i) : start + ramp.length + (i - steps + 0.5) * (ramp.length / steps),
    );
    const h = wingH(i);
    e.set(0, s.dir, 0);
    q.setFromEuler(e);
    for (const side of [-1, 1] as const) {
      p.set(
        s.x + s.rightX * side * wingLat,
        s.y + h / 2,
        s.z + s.rightZ * side * wingLat,
      );
      sc.set(TKTS.wingWidth, Math.max(0.25, h), segLen);
      wings.setMatrixAt(i * 2 + (side < 0 ? 0 : 1), m.compose(p, q, sc));
      litEdge(s, h, side * wingLat, TKTS.wingWidth - 0.3, i * 2 + (side < 0 ? 0 : 1));
    }
    // The centre run's own tread edges (only over the real ramp).
    if (i < steps) litEdge(s, ((along + 0.5) / steps) * ramp.height, 0, ramp.halfWidth * 2 - 0.5, i);
  }
  wings.instanceMatrix.needsUpdate = true;
  bars.instanceMatrix.needsUpdate = true;
  if (bars.instanceColor) bars.instanceColor.needsUpdate = true;
  g.add(wings);
  g.add(bars);

  // --- glass balustrade + pale handrail along each wing's outer edge ---
  const crestA = track.sampleAt(wingAt(0));
  const crestB = track.sampleAt(start + ramp.length + TKTS.landingCourses * (ramp.length / steps));
  const runLen = Math.hypot(crestB.x - crestA.x, crestB.z - crestA.z);
  const mid = track.sampleAt((wingAt(0) + start + ramp.length + TKTS.landingCourses * (ramp.length / steps)) / 2);
  const rise = wingH(courses - 1) - wingH(0);
  const pitch = Math.atan2(rise, runLen);
  const glassMat = new THREE.MeshStandardMaterial({
    color: 0x8e1420,
    roughness: 0.1,
    metalness: 0,
    transparent: true,
    opacity: 0.42,
    emissive: TKTS.glass,
    emissiveIntensity: 0.55,
    side: THREE.DoubleSide,
  });
  const capMat = new THREE.MeshStandardMaterial({ color: 0xd8dbe2, roughness: 0.3, metalness: 0.85 });
  const railLat = ramp.halfWidth + TKTS.wingWidth - 0.16;
  for (const side of [-1, 1] as const) {
    const baseY = mid.y + (wingH(0) + wingH(courses - 1)) / 2;
    const panel = new THREE.Mesh(new THREE.BoxGeometry(0.12, TKTS.railHeight, 1), glassMat);
    panel.setRotationFromEuler(new THREE.Euler(-pitch, mid.dir, 0, 'YXZ'));
    panel.position.set(
      mid.x + mid.rightX * side * railLat,
      baseY + TKTS.railHeight / 2,
      mid.z + mid.rightZ * side * railLat,
    );
    panel.scale.set(1, 1, Math.hypot(runLen, rise));
    g.add(panel);
    const cap = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.16, 1), capMat);
    cap.setRotationFromEuler(new THREE.Euler(-pitch, mid.dir, 0, 'YXZ'));
    cap.position.set(
      mid.x + mid.rightX * side * railLat,
      baseY + TKTS.railHeight + 0.08,
      mid.z + mid.rightZ * side * railLat,
    );
    cap.scale.set(1, 1, Math.hypot(runLen, rise));
    g.add(cap);
  }
}

/** Tiered steps matching the launch ramp's incline (centred, leaving road on
 *  both sides). Dispatches the GS5 identity builds; NYC's ruby stairs by
 *  default; `ramp.color`/`emissive`/`steps`/`build` restyle it per city. */
function buildRampSteps(track: Track, ramp: RampSpec, finish: number): THREE.Group {
  const g = new THREE.Group();
  const start = ramp.at * finish;
  const steps = ramp.steps ?? 7;
  const build = ramp.build;
  const railColor = new THREE.Color(ramp.emissive ?? 0x6a0a12).lerp(new THREE.Color(0xffffff), 0.45);
  const railMat = new THREE.MeshBasicMaterial({ color: railColor });
  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const e = new THREE.Euler();
  const p = new THREE.Vector3();
  const sc = new THREE.Vector3();
  if (build === 'jetty') {
    buildJettyRamp(g, track, ramp, start);
  } else if (build === 'bus') {
    buildBusRamp(g, track, ramp, start);
  } else if (build === 'sand' || build === 'dune') {
    buildSandRamp(g, track, ramp, start, build === 'dune');
  } else if (build === 'snow') {
    buildSnowRamp(g, track, ramp, start);
  } else if (steps === 1) {
    // Legacy smooth wedge fallback (no city ships it since GS5): ONE box
    // PITCHED to the launch slope so its top face IS the incline the physics
    // rides. (The old flat half-height slab left the M99 full-height lip
    // floating in mid-air — measured on the Patagonia snow bank.)
    const mat = new THREE.MeshStandardMaterial({
      color: ramp.color ?? 0xd2202c,
      flatShading: true,
      roughness: 0.5,
      emissive: ramp.emissive ?? 0x6a0a12,
      emissiveIntensity: 0.5,
    });
    g.add(wedgeMesh(track, ramp, start, 1.2, mat));
    const pitch = Math.atan(ramp.height / ramp.length);
    const mid = track.sampleAt(start + ramp.length / 2);
    for (const side of [-1, 1]) {
      const rail = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), railMat);
      rail.setRotationFromEuler(new THREE.Euler(-pitch, mid.dir, 0, 'YXZ'));
      rail.position.set(
        mid.x + mid.rightX * side * (ramp.halfWidth - 0.14),
        mid.y + ramp.height / 2 + 0.09,
        mid.z + mid.rightZ * side * (ramp.halfWidth - 0.14),
      );
      rail.scale.set(0.26, 0.18, Math.hypot(ramp.length, ramp.height));
      g.add(rail);
    }
  } else {
    // Stepped builds: NYC stairs (default) / Tokyo neon-metal / Mumbai
    // basalt / Napoli tufa.
    const mat = steppedRampMaterial(ramp);
    const mesh = new THREE.InstancedMesh(new THREE.BoxGeometry(1, 1, 1), mat, steps);
    // Rails per build: glow (NYC red / Tokyo cyan), warm STONE caps (Napoli),
    // or none at all (Mumbai swaps emissive rails for daylight-proof studs).
    const railsKind: 'glow' | 'stone' | 'none' =
      build === 'tufa' ? 'stone' : build === 'basalt' ? 'none' : 'glow';
    const capMat =
      railsKind === 'stone'
        ? new THREE.MeshStandardMaterial({
            color: 0xe9dcb4,
            roughness: 0.6,
            emissive: 0xffd9a0,
            emissiveIntensity: 0.14,
            flatShading: true,
          })
        : railMat;
    const rails =
      railsKind === 'none'
        ? null
        : new THREE.InstancedMesh(new THREE.BoxGeometry(1, 1, 1), capMat, steps * 2);
    // Tread NOSINGS (basalt/tufa): a pale worn strip along each step's
    // leading top edge — the "these are real stairs" cue in flat daylight.
    const nosing =
      build === 'basalt' || build === 'tufa'
        ? new THREE.InstancedMesh(
            new THREE.BoxGeometry(1, 1, 1),
            new THREE.MeshStandardMaterial({
              color: build === 'basalt' ? 0xb6b2a6 : 0xf2e6c4,
              roughness: 0.72,
              flatShading: true,
            }),
            steps,
          )
        : null;
    // Reflector STUDS (basalt): cat-eye rows down both edges + across the
    // lip — reads by contrast, not glow, so Mumbai's noon can't eat it.
    const studs =
      build === 'basalt'
        ? new THREE.InstancedMesh(
            new THREE.BoxGeometry(0.2, 0.09, 0.28),
            new THREE.MeshBasicMaterial({ color: 0xfff3d0, toneMapped: false }),
            steps * 4 + 6,
          )
        : null;
    let studI = 0;
    for (let i = 0; i < steps; i++) {
      const t = (i + 0.5) / steps;
      const s = track.sampleAt(start + t * ramp.length);
      const h = t * ramp.height;
      e.set(0, s.dir, 0);
      q.setFromEuler(e);
      p.set(s.x, s.y + h / 2, s.z);
      const segLen = ramp.length / steps + 0.4;
      sc.set(ramp.halfWidth * 2, Math.max(0.2, h), segLen);
      mesh.setMatrixAt(i, m.compose(p, q, sc));
      if (rails) {
        for (const side of [-1, 1]) {
          const rx = s.x + s.rightX * side * (ramp.halfWidth - 0.14);
          const rz = s.z + s.rightZ * side * (ramp.halfWidth - 0.14);
          p.set(rx, s.y + h + 0.09, rz);
          sc.set(0.26, railsKind === 'stone' ? 0.24 : 0.18, segLen);
          rails.setMatrixAt(i * 2 + (side < 0 ? 0 : 1), m.compose(p, q, sc));
        }
      }
      if (nosing) {
        const edge = track.sampleAt(start + ((i + 1) / steps) * ramp.length - 0.2);
        e.set(0, edge.dir, 0);
        q.setFromEuler(e);
        p.set(edge.x, edge.y + h + 0.035, edge.z);
        sc.set(ramp.halfWidth * 2 - 0.2, 0.07, 0.4);
        nosing.setMatrixAt(i, m.compose(p, q, sc));
      }
      if (studs) {
        const fx = Math.sin(s.dir); // §6.1: heading → forward (sin, cos)
        const fz = Math.cos(s.dir);
        for (const [side, dz] of [
          [-1, -0.9],
          [1, -0.9],
          [-1, 0.9],
          [1, 0.9],
        ] as const) {
          const sx = s.x + s.rightX * side * (ramp.halfWidth - 0.42);
          const sz = s.z + s.rightZ * side * (ramp.halfWidth - 0.42);
          p.set(sx + fx * dz, s.y + h + 0.05, sz + fz * dz);
          sc.set(1, 1, 1);
          studs.setMatrixAt(studI++, m.compose(p, q, sc));
        }
      }
    }
    if (studs) {
      // The lip row: six studs across the launch edge.
      const lipS = track.sampleAt(start + ramp.length - 0.3);
      e.set(0, lipS.dir, 0);
      q.setFromEuler(e);
      for (let k = 0; k < 6; k++) {
        const lat = -ramp.halfWidth + 0.6 + (k * (ramp.halfWidth * 2 - 1.2)) / 5;
        p.set(
          lipS.x + lipS.rightX * lat,
          lipS.y + ramp.height + 0.05,
          lipS.z + lipS.rightZ * lat,
        );
        sc.set(1, 1, 1);
        studs.setMatrixAt(studI++, m.compose(p, q, sc));
      }
      studs.instanceMatrix.needsUpdate = true;
      g.add(studs);
    }
    mesh.instanceMatrix.needsUpdate = true;
    g.add(mesh);
    if (rails) {
      rails.instanceMatrix.needsUpdate = true;
      g.add(rails);
    }
    if (nosing) {
      nosing.instanceMatrix.needsUpdate = true;
      g.add(nosing);
    }
    // NYC only (no other city ships the default build): the TKTS grandstand —
    // flanking wings that carry the silhouette well above the physics ramp, and
    // back-lit tread edges that hold up against Times Square. See buildTktsStair.
    if (build === undefined) buildTktsStair(g, track, ramp, start, steps, mat);
  }
  // M99: the LIP — a bar across the launch edge plus a checkered takeoff
  // strip on the top surface (the finish-line language: "send it"). GS5: the
  // bar's material follows the build — glow (NYC/Tokyo), warm stone
  // (Napoli), painted kerb (Mumbai), timber bull-nose (the jetty) — and the
  // heaped builds (sand/dune/snow) + the bus skip it entirely (a glowing bar
  // on a dune/bus roof breaks the object read; the checker strip remains
  // their cue).
  const lipKind: 'glow' | 'stone' | 'kerb' | 'timber' | 'none' =
    build === 'jetty'
      ? 'timber'
      : build === 'tufa'
        ? 'stone'
        : build === 'basalt'
          ? 'kerb'
          : build === undefined || build === 'neon'
            ? 'glow'
            : 'none';
  const lip = track.sampleAt(start + ramp.length);
  e.set(0, lip.dir, 0);
  if (lipKind !== 'none') {
    const jetty = lipKind === 'timber';
    const lipMat =
      lipKind === 'glow'
        ? railMat
        : lipKind === 'stone'
          ? new THREE.MeshStandardMaterial({ color: 0xe9dcb4, roughness: 0.6, flatShading: true })
          : lipKind === 'kerb'
            ? new THREE.MeshStandardMaterial({ color: 0xe4e0d4, roughness: 0.65, flatShading: true })
            : new THREE.MeshStandardMaterial({ color: 0x6b4a2c, roughness: 0.88 });
    const lipBar = new THREE.Mesh(
      new THREE.BoxGeometry(ramp.halfWidth * 2, jetty ? 0.34 : 0.22, jetty ? 0.5 : 0.3),
      lipMat,
    );
    lipBar.position.set(lip.x, lip.y + ramp.height + (jetty ? 0.02 : 0.08), lip.z);
    lipBar.setRotationFromEuler(e);
    g.add(lipBar);
  }
  // The checkered takeoff strip. The JETTY skips it: its deck's top face IS
  // the incline, so a decal there z-fights into fragments (measured), and the
  // bull-nose beam plus the boost strip already say "launch" in timber.
  if (build !== 'jetty') {
    const checkerMat = new THREE.MeshStandardMaterial({ map: checkerTexture(), roughness: 0.55 });
    const strip = new THREE.Mesh(new THREE.PlaneGeometry(ramp.halfWidth * 2, 1.1), checkerMat);
    // Lie the strip ON the incline surface just up-course of the lip: tilt the
    // flat decal by the ramp's slope so it hugs the take-off face.
    const slope = ramp.height / ramp.length;
    strip.rotation.x = -Math.PI / 2 - Math.atan(slope);
    const stripHolder = new THREE.Group();
    stripHolder.position.set(lip.x, lip.y + ramp.height, lip.z);
    stripHolder.setRotationFromEuler(e);
    strip.position.set(0, -0.7 * slope + 0.06, -0.7); // 0.7 u back down the face
    stripHolder.add(strip);
    g.add(stripHolder);
  }
  return g;
}

/** A hanging fishing net (transparent knotted mesh) for the Lagos pier. */
function netTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 64;
  c.height = 64;
  const x = c.getContext('2d') as CanvasRenderingContext2D;
  x.clearRect(0, 0, 64, 64);
  x.strokeStyle = 'rgba(206,198,168,0.8)';
  x.lineWidth = 1.1;
  for (let i = -64; i < 64; i += 7) {
    x.beginPath();
    x.moveTo(i, 0);
    x.lineTo(i + 64, 64);
    x.stroke();
    x.beginPath();
    x.moveTo(i + 64, 0);
    x.lineTo(i, 64);
    x.stroke();
  }
  // A torn corner, so the net never reads as a printed panel.
  x.globalCompositeOperation = 'destination-out';
  x.beginPath();
  x.moveTo(46, 64);
  x.lineTo(64, 40);
  x.lineTo(64, 64);
  x.closePath();
  x.fill();
  x.globalCompositeOperation = 'source-over';
  return canvasTex(c);
}

/**
 * The JETTY build for a launch ramp (M117d, owner: "I'd like the Lagos ramp
 * to be better designed with texture") — Lagos' causeway pier, in timber: a
 * planked deck whose boards run ACROSS the run, stringer beams down both
 * edges, piles carrying it with a lower waling beam, and mooring posts with
 * rope rings framing the launch edge. Physics is untouched (the deck's TOP
 * FACE is the same smooth incline the ramp always was — the M99 lesson); this
 * is skin only, and it wears the SAME boards as the sky-path it launches you
 * onto, so the whole crossing reads as one structure.
 *
 * M117f — owner: "the ramps still look too much like ramps". Lagos is not
 * roadworks; the equivalent note here is that it must read as a WORKING PIER
 * someone happens to drive off, not a skate ramp. Three moves: the deck's
 * edges are no longer a ruled line (sawn-off board ends overhang unevenly and
 * replacement boards sit proud), the structure grows the things a pier has
 * (cross-braced piles, a bull-rail, cleats, coiled rope, nets and floats on
 * the mooring posts), and the LAGOON around it becomes a landing — crates,
 * drums, a hauled-out pirogue, a drying rack and a slipway, all out over the
 * water beyond the guardrail so nothing solid stands in the run-up.
 */
function buildJettyRamp(g: THREE.Group, track: Track, ramp: RampSpec, start: number): void {
  const slopeLen = Math.hypot(ramp.length, ramp.height);
  const pitch = Math.atan(ramp.height / ramp.length);
  const cosP = Math.cos(pitch);
  const planks = plankTexture();
  planks.repeat.set(1, Math.max(2, Math.round(slopeLen / 6)));
  // Weathered down: fresh orange boards read as a built ramp; a grey-brown
  // wash reads as decking that has been out in the lagoon for years.
  const deckMat = new THREE.MeshStandardMaterial({ map: planks, color: 0xa4907a, roughness: 0.94 });
  const beamMat = new THREE.MeshStandardMaterial({ color: 0x5a3f26, roughness: 0.9, flatShading: true });
  const pileMat = new THREE.MeshStandardMaterial({ color: 0x46311d, roughness: 0.95, flatShading: true });
  const mid = track.sampleAt(start + ramp.length / 2);
  const e = new THREE.Euler(-pitch, mid.dir, 0, 'YXZ');

  // The deck slab — its TOP face IS the drivable incline.
  const THICK = 0.85;
  const deck = new THREE.Mesh(new THREE.BoxGeometry(ramp.halfWidth * 2, THICK, slopeLen), deckMat);
  deck.setRotationFromEuler(e);
  deck.position.set(mid.x, mid.y + ramp.height / 2 - (THICK / 2) * cosP, mid.z);
  g.add(deck);

  // Stringer beams along both deck edges + a lower waling beam on the piles.
  for (const side of [-1, 1] as const) {
    for (const [inset, dy, w, h] of [
      [0.2, 0.24, 0.36, 0.6],
      [0.5, -1.05, 0.28, 0.34],
    ] as const) {
      const beam = new THREE.Mesh(new THREE.BoxGeometry(w, h, slopeLen), beamMat);
      beam.setRotationFromEuler(e);
      beam.position.set(
        mid.x + mid.rightX * side * (ramp.halfWidth - inset),
        mid.y + ramp.height / 2 + dy,
        mid.z + mid.rightZ * side * (ramp.halfWidth - inset),
      );
      g.add(beam);
    }
  }

  // SAWN-OFF BOARD ENDS overhanging both edges by a jittered amount — the one
  // change that stops the deck reading as a moulded wedge: a pier's edge is a
  // ragged line of plank ends, never a ruled one.
  const kit = new BoxKit();
  const boardMat = new THREE.MeshStandardMaterial({ color: 0x7d5730, roughness: 0.94, flatShading: true });
  const paleMat = new THREE.MeshStandardMaterial({ color: 0xc0a377, roughness: 0.9, flatShading: true });
  for (const side of [-1, 1] as const) {
    for (let i = 0; i < 16; i++) {
      const t = 0.02 + (i / 15) * 0.96;
      const s = track.sampleAt(start + t * ramp.length);
      const out = 0.12 + art(i * 7 + (side > 0 ? 41 : 3)) * 0.55;
      const lat = side * (ramp.halfWidth + out / 2 - 0.05);
      kit.add(
        boardMat,
        s.x + s.rightX * lat,
        s.y + t * ramp.height - 0.16,
        s.z + s.rightZ * lat,
        s.dir,
        out + 0.1,
        0.2,
        0.42 + art(i * 11) * 0.2,
        -pitch,
      );
    }
  }
  // A BULL-RAIL down each edge (the timber kerb every working pier has) and a
  // short flat apron at the foot, so the deck stops meeting the road on a
  // knife edge — the profile that made it read as a purpose-built kicker.
  for (const side of [-1, 1] as const) {
    const lat = side * (ramp.halfWidth + 0.16);
    kit.add(
      beamMat,
      mid.x + mid.rightX * lat,
      mid.y + ramp.height / 2 + 0.16,
      mid.z + mid.rightZ * lat,
      mid.dir,
      0.24,
      0.34,
      slopeLen,
      -pitch,
    );
  }
  // BOLLARDS AND ROPE down each edge — the quay detail that reads "pier" at a
  // glance, and the one thing a skate ramp never has. Kept low (0.6 u) and
  // tight to the deck edge so the jumpable width is untouched.
  for (const side of [-1, 1] as const) {
    const lat = side * (ramp.halfWidth + 0.42);
    const N = 6;
    const post: { x: number; y: number; z: number }[] = [];
    for (let i = 0; i <= N; i++) {
      const t = i / N;
      const s = track.sampleAt(start + t * ramp.length);
      const px = s.x + s.rightX * lat;
      const pz = s.z + s.rightZ * lat;
      const py = s.y + t * ramp.height;
      kit.add(pileMat, px, py + 0.3, pz, s.dir, 0.24, 0.85, 0.24);
      post.push({ x: px, y: py + 0.52, z: pz });
    }
    for (let i = 1; i <= N; i++) {
      const a = post[i - 1];
      const b = post[i];
      // Two segments per bay so the rope sags instead of ruling a line.
      for (const [t0, t1, dip] of [
        [0, 0.5, 0.14],
        [0.5, 1, 0.14],
      ] as const) {
        const ax = a.x + (b.x - a.x) * t0;
        const az = a.z + (b.z - a.z) * t0;
        const bx = a.x + (b.x - a.x) * t1;
        const bz = a.z + (b.z - a.z) * t1;
        const ay = a.y + (b.y - a.y) * t0 - (t0 === 0 ? 0 : dip);
        const by = a.y + (b.y - a.y) * t1 - (t1 === 1 ? 0 : dip);
        const len = Math.hypot(bx - ax, bz - az);
        kit.add(
          beamMat,
          (ax + bx) / 2,
          (ay + by) / 2,
          (az + bz) / 2,
          Math.atan2(bx - ax, bz - az),
          0.08,
          0.08,
          Math.hypot(len, by - ay),
          -Math.atan2(by - ay, len),
        );
      }
    }
  }

  {
    const foot = track.sampleAt(start - 0.8);
    // Muted, not pale: a cream apron across the full width read as a painted
    // start line at the foot of the ramp (measured on the approach).
    kit.add(beamMat, foot.x, foot.y + 0.09, foot.z, foot.dir, ramp.halfWidth * 2 + 0.5, 0.18, 2.0);
    // Shingle washed up against the foot corners.
    for (let i = 0; i < 6; i++) {
      const s2 = i % 2 === 0 ? 1 : -1;
      const f = track.sampleAt(start + art(i * 5) * 3.2 - 0.6);
      const lat = s2 * (ramp.halfWidth + 0.3 + art(i * 9) * 1.4);
      kit.add(
        pileMat,
        f.x + f.rightX * lat,
        f.y + 0.16,
        f.z + f.rightZ * lat,
        f.dir + art(i * 11) * 3,
        0.7 + art(i * 13) * 1.1,
        0.34,
        0.7 + art(i * 17) * 1.0,
      );
    }
  }

  // A few pale REPLACEMENT boards laid proud across the run, slightly askew —
  // the pier has been patched, it was not manufactured.
  for (let i = 0; i < 4; i++) {
    const t = 0.14 + art(i * 13) * 0.72;
    const s = track.sampleAt(start + t * ramp.length);
    const lat = (art(i * 17) - 0.5) * ramp.halfWidth;
    kit.add(
      paleMat,
      s.x + s.rightX * lat,
      s.y + t * ramp.height + 0.04,
      s.z + s.rightZ * lat,
      s.dir + (art(i * 19) - 0.5) * 0.09,
      2.4 + art(i * 23) * 4,
      0.1,
      0.44,
      -pitch,
    );
  }

  // Piles: the legs under both edges, sunk into the causeway, CROSS-BRACED.
  const pileTop: { x: number; y: number; z: number }[][] = [[], []];
  for (const side of [-1, 1] as const) {
    for (const t of [0.2, 0.5, 0.8]) {
      const s = track.sampleAt(start + t * ramp.length);
      const px = s.x + s.rightX * side * (ramp.halfWidth - 0.5);
      const pz = s.z + s.rightZ * side * (ramp.halfWidth - 0.5);
      const top = s.y + t * ramp.height - 0.5;
      const base = track.heightAt(px, pz) - 0.7;
      const h = Math.max(0.5, top - base);
      const pile = new THREE.Mesh(new THREE.CylinderGeometry(0.26, 0.3, h, 6), pileMat);
      pile.position.set(px, base + h / 2, pz);
      g.add(pile);
      pileTop[side > 0 ? 1 : 0].push({ x: px, y: base + h, z: pz });
    }
  }
  for (const row of pileTop) {
    for (let i = 1; i < row.length; i++) {
      const a = row[i - 1];
      const b = row[i];
      const span = Math.hypot(b.x - a.x, b.z - a.z);
      const yaw = Math.atan2(b.x - a.x, b.z - a.z);
      for (const lean of [-1, 1] as const) {
        kit.add(
          beamMat,
          (a.x + b.x) / 2,
          (a.y + b.y) / 2 - 0.75,
          (a.z + b.z) / 2,
          yaw,
          0.13,
          0.13,
          Math.hypot(span, 1.5),
          (lean * Math.atan2(1.5, span)) / 1,
        );
      }
    }
  }

  // Mooring posts framing the launch edge (just OUTSIDE the jumpable width,
  // so they dress the lip without standing in it) + rope rings, hung nets and
  // floats: pier furniture, so the lip reads as a pier head.
  const lip = track.sampleAt(start + ramp.length);
  const netMat = new THREE.MeshStandardMaterial({
    map: netTexture(),
    roughness: 0.95,
    alphaTest: 0.35,
    side: THREE.DoubleSide,
  });
  const floatGeo = new THREE.SphereGeometry(0.22, 7, 5);
  for (const side of [-1, 1] as const) {
    const px = lip.x + lip.rightX * side * (ramp.halfWidth + 0.55);
    const pz = lip.z + lip.rightZ * side * (ramp.halfWidth + 0.55);
    const base = track.heightAt(px, pz);
    const h = ramp.height + 1.15;
    const post = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.34, h, 7), pileMat);
    post.position.set(px, base + h / 2, pz);
    g.add(post);
    const ring = new THREE.Mesh(new THREE.TorusGeometry(0.31, 0.075, 5, 10), beamMat);
    ring.position.set(px, base + h - 0.42, pz);
    ring.rotation.x = -Math.PI / 2;
    g.add(ring);
    // A net hung down the OUTBOARD face of the post (never across the run —
    // nothing may span the drive lane).
    const net = new THREE.Mesh(new THREE.PlaneGeometry(1.5, 2.1), netMat);
    net.position.set(
      px + lip.rightX * side * 0.42,
      base + h - 1.5,
      pz + lip.rightZ * side * 0.42,
    );
    net.rotation.y = lip.dir + Math.PI / 2;
    g.add(net);
    for (let i = 0; i < 3; i++) {
      const f = new THREE.Mesh(floatGeo, i % 2 ? paleMat : beamMat);
      f.position.set(
        px + lip.rightX * side * (0.5 + art(i * 5) * 0.3),
        base + h - 0.9 - i * 0.5,
        pz + lip.rightZ * side * (0.5 + art(i * 5) * 0.3) + (art(i * 7) - 0.5) * 0.6,
      );
      g.add(f);
    }
    // A coil of rope flaked on the deck edge, and a timber cleat.
    const cm = track.sampleAt(start + ramp.length * 0.72);
    const clat = side * (ramp.halfWidth + 0.38);
    const coil = new THREE.Mesh(new THREE.TorusGeometry(0.42, 0.09, 5, 12), beamMat);
    coil.position.set(cm.x + cm.rightX * clat, cm.y + ramp.height * 0.72 + 0.1, cm.z + cm.rightZ * clat);
    coil.rotation.x = -Math.PI / 2 - pitch;
    coil.rotation.z = art(side + 3) * 2;
    g.add(coil);
  }

  // ---- THE LANDING: the pier's working yard, out over the lagoon ---------
  // Everything here sits beyond the guardrail (|lateral| ≥ 19.5), so a car
  // coming down the causeway can never meet it.
  const at = (arc: number, lat: number): { x: number; y: number; z: number; dir: number } => {
    const s = track.sampleAt(arc);
    const x = s.x + s.rightX * lat;
    const z = s.z + s.rightZ * lat;
    return { x, y: track.heightAt(x, z), z, dir: s.dir };
  };
  const crateMat = new THREE.MeshStandardMaterial({ color: 0x9fb6bd, roughness: 0.85, flatShading: true });
  const drumMat = new THREE.MeshStandardMaterial({ color: 0x2f6f4a, roughness: 0.8, flatShading: true });
  const mid2 = start + ramp.length / 2;

  // THE STAGING: a planked landing on piles out over the lagoon beside the
  // ramp. MEASURED: pier gear scattered on the bank at water level vanished
  // behind the guardrail's chevron band from the driver's eye — the whole
  // point is that you see a working pier as you approach, so the yard is
  // raised 1.5 u onto its own deck.
  const stageDeckMat = new THREE.MeshStandardMaterial({ map: plankTexture(), roughness: 0.93 });
  for (const side of [-1, 1] as const) {
    const near = side > 0;
    const p = at(mid2 + (near ? 1 : -6), side * 24);
    const deckY = p.y + 1.5;
    const halfL = near ? 6.5 : 4.5;
    const halfW = near ? 4.5 : 3.5;
    const plat = new THREE.Mesh(new THREE.BoxGeometry(halfW * 2, 0.42, halfL * 2), stageDeckMat);
    plat.position.set(p.x, deckY, p.z);
    plat.rotation.y = p.dir;
    g.add(plat);
    for (const ox of [-halfW + 0.7, halfW - 0.7]) {
      for (const oz of [-halfL + 0.8, 0, halfL - 0.8]) {
        const lx = p.x + Math.cos(p.dir) * ox + Math.sin(p.dir) * oz;
        const lz = p.z - Math.sin(p.dir) * ox + Math.cos(p.dir) * oz;
        const h = deckY - 0.2 - (track.heightAt(lx, lz) - 0.8);
        kit.add(pileMat, lx, deckY - 0.2 - h / 2, lz, p.dir, 0.4, h, 0.4);
      }
    }
    // Crates and drums stacked on it, and a bull-rail round the outer edge.
    for (let i = 0; i < (near ? 7 : 4); i++) {
      const ox = (art(i * 3 + (near ? 0 : 31)) - 0.5) * (halfW * 1.3);
      const oz = (art(i * 5 + (near ? 0 : 31)) - 0.5) * (halfL * 1.5);
      const lx = p.x + Math.cos(p.dir) * ox + Math.sin(p.dir) * oz;
      const lz = p.z - Math.sin(p.dir) * ox + Math.cos(p.dir) * oz;
      kit.add(
        i % 3 === 0 ? drumMat : crateMat,
        lx,
        deckY + 0.5 + (i % 2) * 0.62,
        lz,
        p.dir + art(i * 7) * 1.3,
        1.0,
        0.6,
        1.3,
      );
    }
    kit.add(beamMat, p.x + Math.cos(p.dir) * side * halfW, deckY + 0.45, p.z - Math.sin(p.dir) * side * halfW, p.dir, 0.2, 0.5, halfL * 2);
  }

  // A drying rack with a net between two poles, on the near staging.
  const rack = at(mid2 + 6, 24);
  for (const ox of [-2.4, 2.4]) {
    kit.add(pileMat, rack.x + Math.sin(rack.dir) * ox, rack.y + 2.8, rack.z + Math.cos(rack.dir) * ox, rack.dir, 0.22, 3.0, 0.22);
  }
  kit.add(pileMat, rack.x, rack.y + 4.2, rack.z, rack.dir, 0.18, 0.18, 5.0);
  const dry = new THREE.Mesh(new THREE.PlaneGeometry(4.6, 2.1), netMat);
  dry.position.set(rack.x, rack.y + 3.1, rack.z);
  dry.rotation.y = rack.dir;
  g.add(dry);

  // A hauled-out pirogue floating alongside, and mooring bollards on the kerb.
  const boat = at(mid2 + 12, 21.5);
  const boatYaw = boat.dir + 0.35;
  kit.local(beamMat, boat, boatYaw, 0, 0.5, 0, 1.5, 0.5, 7.0); // hull
  kit.local(pileMat, boat, boatYaw, 0, 0.8, 2.6, 0.9, 0.3, 1.8); // bow deck
  kit.local(pileMat, boat, boatYaw, 0, 0.8, -2.6, 0.9, 0.3, 1.6); // stern deck
  for (const oz of [-1.2, 0.6]) kit.local(paleMat, boat, boatYaw, 0, 0.86, oz, 1.4, 0.12, 0.3); // thwarts
  kit.local(drumMat, boat, boatYaw, 0, 1.1, -3.3, 0.5, 0.7, 0.5); // outboard
  for (let i = 0; i < 4; i++) {
    const p = at(mid2 - 6 + i * 5, (i % 2 ? 1 : -1) * 19.4);
    kit.add(pileMat, p.x, p.y + 0.55, p.z, p.dir, 0.36, 1.1, 0.36);
  }
  kit.flush(g);
}

// ---- GS6: the shortcut-corridor rooftop kit ---------------------------------

/** Max rooftop-prop instances per city (bounded draw-call budget). */
const ROOF_PROP_CAP = 150;
/** How far (u) a tower may stand from the shortcut spine and still dress. */
const ROOF_CORRIDOR_R = 48;
/** Below this a block's roof never reads from the deck (and Asakusa's 4.5-8 u
 *  machiya wear tiled roofs already — plant on them would be wrong, not just
 *  invisible). */
const ROOF_MIN_H = 9;

/** One dressable roof: the placed block plus its STABLE hash index. */
export interface RoofSpot {
  b: BoxInst;
  i: number;
}

/**
 * PURE: which already-placed blocks the rooftop kit dresses, in emit order.
 *
 * BOTH box families are searched — and that is the whole point. `a.buildings`
 * and `a.adBuildings` are two separate instance arrays (plain vs facade-lit
 * boxes), and which one a zone pushes into is a MATERIAL choice, invisible to
 * a corridor query. The kit originally read `a.buildings` alone, so in Tokyo
 * it saw only Asakusa's 4.5-8 u machiya (all under ROOF_MIN_H) while every
 * Shibuya neon tower the expressway deck flies over — 20-44 u, pushed to
 * `a.adBuildings` — was invisible to it: the kit emitted ZERO props in the
 * one city whose money shot is the view down onto those roofs. Measured on
 * both seeds: 0 eligible from `buildings`, 20-26 from `adBuildings`.
 *
 * `buildings` is walked FIRST so its hash indices are unchanged (NYC's High
 * Line corridor is all `buildings`, and its existing dressing must stay byte
 * for byte); `adBuildings` indices continue past the end of that list.
 */
export function rooftopKitSpots(
  buildings: readonly BoxInst[],
  adBuildings: readonly BoxInst[],
  shortcuts: readonly PlacedShortcut[],
  cityId: string,
): RoofSpot[] {
  if (!shortcuts.length || (cityId !== 'nyc' && cityId !== 'tokyo')) return [];
  const pts = shortcuts.flatMap((sc) => sc.surfacePts);
  if (!pts.length) return [];
  const spots: RoofSpot[] = [];
  const sweep = (list: readonly BoxInst[], base: number): void => {
    list.forEach((b, k) => {
      if (b.h < ROOF_MIN_H) return;
      for (const pt of pts) {
        const dx = b.x - pt.x;
        const dz = b.z - pt.z;
        if (dx * dx + dz * dz < ROOF_CORRIDOR_R * ROOF_CORRIDOR_R) {
          spots.push({ b, i: base + k });
          return;
        }
      }
    });
  };
  sweep(buildings, 0);
  sweep(adBuildings, buildings.length);
  return spots;
}

/**
 * Rooftop furniture on the towers UNDER the aerial shortcut corridors (the
 * M117 NYC highline + Tokyo expressway are the only places the camera rides
 * ABOVE the skyline and stares at rooftops). AC units + a city signature
 * (NYC water tanks / Tokyo antenna masts with their red aircraft beacons) +
 * the odd dim vent glow, INSTANCED into ≤4 draw calls per city and capped.
 * Fully deterministic: variety comes from propHash over the already-placed
 * building list — the layout rng stream is never touched (the GS law).
 */
function buildRooftopKit(
  buildings: readonly BoxInst[],
  adBuildings: readonly BoxInst[],
  shortcuts: readonly PlacedShortcut[],
  cityId: string,
): THREE.Group | null {
  const spots = rooftopKitSpots(buildings, adBuildings, shortcuts, cityId);
  if (!spots.length) return null;
  const g = new THREE.Group();
  const acMat = new THREE.MeshStandardMaterial({ color: 0x767c84, roughness: 0.8, flatShading: true });
  const tankWood = new THREE.MeshStandardMaterial({ color: 0x6e5138, roughness: 0.92, flatShading: true });
  const tankCapM = new THREE.MeshStandardMaterial({ color: 0x2c2f35, roughness: 0.85, flatShading: true });
  // A dim warm vent glow — deliberately UNDER the bloom threshold (GS6 is the
  // emissive-discipline system; rooftops must not become new light noise).
  const ventMat = new THREE.MeshBasicMaterial({ color: 0x8a5a26 });
  // Tokyo's signature instead of the NYC tank: a lattice antenna mast with the
  // red aircraft-warning beacon every real Tokyo roofline carries. A 0.7 u AC
  // box alone is ~nothing from a deck 20 u up at night; the mast gives each
  // roof a vertical and the beacon a legible (still sub-bloom) point of red.
  const mastMat = new THREE.MeshStandardMaterial({ color: 0x4b515a, roughness: 0.72, flatShading: true });
  const beaconMat = new THREE.MeshBasicMaterial({ color: 0xc0342e });
  const acXf: THREE.Matrix4[] = [];
  const tankXf: THREE.Matrix4[] = [];
  const capXf: THREE.Matrix4[] = [];
  const mastXf: THREE.Matrix4[] = [];
  const beaconXf: THREE.Matrix4[] = [];
  const ventXf: THREE.Matrix4[] = [];
  const P = new THREE.Vector3();
  const Q = new THREE.Quaternion();
  const E = new THREE.Euler();
  const S = new THREE.Vector3();
  let total = 0;
  for (const { b, i } of spots) {
    if (total >= ROOF_PROP_CAP) break;
    const topY = b.y + b.h;
    const cos = Math.cos(b.rotY);
    const sin = Math.sin(b.rotY);
    /** A roof-local (ox, oz) offset → world, staying inside the footprint. */
    const place = (ox: number, oz: number): [number, number] => [
      b.x + cos * ox + sin * oz,
      b.z - sin * ox + cos * oz,
    ];
    const n = 1 + (propHash(i * 5 + 1) > 0.55 ? 1 : 0);
    for (let k = 0; k < n && total < ROOF_PROP_CAP; k++) {
      const ox = (propHash(i * 7 + k * 3 + 2) - 0.5) * Math.max(0, b.w - 2.4);
      const oz = (propHash(i * 11 + k * 5 + 3) - 0.5) * Math.max(0, b.d - 2.4);
      const [wx, wz] = place(ox, oz);
      E.set(0, b.rotY + (propHash(i * 13 + k) - 0.5) * 0.9, 0);
      Q.setFromEuler(E);
      const roll = propHash(i * 17 + k * 7 + 4);
      if (cityId === 'nyc' && roll > 0.62 && b.h > 14) {
        // The NYC water tank: wood barrel + dark conical cap.
        const r = 0.85 + propHash(i * 19 + k) * 0.35;
        P.set(wx, topY + r * 1.0, wz);
        S.set(r, r * 1.9, r);
        tankXf.push(new THREE.Matrix4().compose(P, Q, S));
        P.set(wx, topY + r * 2.1, wz);
        S.set(r * 1.12, r * 0.75, r * 1.12);
        capXf.push(new THREE.Matrix4().compose(P, Q, S));
      } else if (cityId === 'tokyo' && roll > 0.58 && b.h > 18) {
        // Antenna mast + its beacon (one prop, two instances — the beacon is
        // never emitted without its mast, so it can't float).
        const hM = 3.0 + propHash(i * 19 + k) * 2.4;
        P.set(wx, topY + hM / 2, wz);
        S.set(0.22, hM, 0.22);
        mastXf.push(new THREE.Matrix4().compose(P, Q, S));
        P.set(wx, topY + hM + 0.18, wz);
        S.set(0.32, 0.32, 0.32);
        beaconXf.push(new THREE.Matrix4().compose(P, Q, S));
      } else {
        // AC / plant unit — the universal rooftop box.
        const w = 0.8 + propHash(i * 23 + k) * 1.1;
        P.set(wx, topY + 0.35, wz);
        S.set(w, 0.7, w * (0.7 + propHash(i * 29 + k) * 0.5));
        acXf.push(new THREE.Matrix4().compose(P, Q, S));
      }
      total++;
    }
    // The odd dim vent glow (~1 in 4 roofs) — life, not light pollution.
    if (propHash(i * 31 + 5) > 0.75 && total < ROOF_PROP_CAP) {
      const [wx, wz] = place(b.w * 0.22, -b.d * 0.18);
      E.set(0, b.rotY, 0);
      Q.setFromEuler(E);
      P.set(wx, topY + 0.16, wz);
      S.set(0.42, 0.3, 0.42);
      ventXf.push(new THREE.Matrix4().compose(P, Q, S));
      total++;
    }
  }
  const flushInst = (
    geo: THREE.BufferGeometry,
    mat: THREE.Material,
    xf: THREE.Matrix4[],
  ): void => {
    if (!xf.length) return;
    const mesh = new THREE.InstancedMesh(geo, mat, xf.length);
    xf.forEach((x, j) => mesh.setMatrixAt(j, x));
    mesh.instanceMatrix.needsUpdate = true;
    g.add(mesh);
  };
  // ≤4 instanced draws PER CITY: NYC spends its budget on ac/tank/cap/vent,
  // Tokyo on ac/mast/beacon/vent (flushInst skips the empty families).
  flushInst(new THREE.BoxGeometry(1, 1, 1), acMat, acXf);
  flushInst(new THREE.CylinderGeometry(1, 1, 1, 9), tankWood, tankXf);
  flushInst(new THREE.ConeGeometry(1, 1, 9), tankCapM, capXf);
  flushInst(new THREE.BoxGeometry(1, 1, 1), mastMat, mastXf);
  flushInst(new THREE.OctahedronGeometry(1, 0), beaconMat, beaconXf);
  flushInst(new THREE.BoxGeometry(1, 1, 1), ventMat, ventXf);
  return g;
}
